import { useState, useEffect, useMemo, useRef } from "react"
import { createPortal } from "react-dom"
import type { FormEvent } from "react"
import {
  Search, Loader2, Building2, Globe, Mail, Phone, MapPin,
  Sparkles, Package, ShieldCheck, X, AlertCircle,
  Zap, Lock, BadgeCheck, Send, Bookmark, ChevronRight, TrendingUp, SlidersHorizontal,
  ArrowLeft, ChevronLeft, Layers, Hash, FolderOpen, Share2, Heart, Calendar, Star, Users, CheckCircle2, Clock, Shield, Award, Copy, Landmark, ExternalLink, Tag, FileText, BadgePercent
} from "lucide-react"
import { useSearch, useNavigate } from "@tanstack/react-router"
import { useQueryClient } from "@tanstack/react-query"
import { useSearchSuppliers } from "@/hooks/buyer/useSearchSuppliers"
import { useProductCategories } from "@/hooks/buyer/useProductCategories"
import { useProductSubcategories } from "@/hooks/buyer/useProductSubcategories"
import { useProductsByCategory } from "@/hooks/buyer/useProductsByCategory"
import { useProductsBySubcategory } from "@/hooks/buyer/useProductsBySubcategory"
import { useCreateRfq } from "@/hooks/buyer/useCreateRfq"
import { useAuthStore } from "@/store/auth.store"
import { useRecentSearches } from "@/hooks/buyer/useRecentSearches"
import { useProductDetails } from "@/hooks/seller/useProductDetails"
import { useBuyerUIStore } from "@/store/buyer-ui.store"
import { useGetCompanies } from "@/hooks/admin/useGetCompanies"
import { useGetBuyerRfqs } from "@/hooks/buyer/useGetBuyerRfqs"
import { SupplierCard } from "@/components/dashboard/shared/SupplierCard"
import { WhySourceWithEnga } from "@/components/dashboard/shared/WhySourceWithEnga"
import { getCategoryIcon } from "./categoryIcons"
import { ProductSearchCard } from "@/components/dashboard/shared/ProductSearchCard"
import { useIsMobile } from "@/hooks/useIsMobile"
import type { CompanySearchResult, ProductSearchResult, ExternalProductSearchResult, ExternalSeller, LeadSeller } from "@/lib/api/search.api"
import { cn } from "@/lib/utils"
import { DatePicker } from "@/components/ui/date-picker"
import { useSearchLocation } from "@/hooks/useSearchLocation"
import { SubmitRfqPage } from "./SubmitRfqPage"
import { PromptModalCard } from "@/components/shared/PromptModalCard"
import { useCompanyDetails } from "@/hooks/seller/useCompanyDetails"
import { useExternalSeller } from "@/hooks/buyer/useExternalSeller"
import { useSearchFilters } from "@/hooks/buyer/useSearchFilters"
import { useProductVariantDetails } from "@/hooks/seller/useProductVariantDetails"
import { useFindSupplierStore } from "@/store/find-supplier.store"
import type { ProductImage } from "@/lib/api/product.api"

// ── Radial Match Score ────────────────────────────────────────────────────────
function MatchScore({ pct }: { pct: number }) {
  const deg = Math.round((pct / 100) * 360)
  return (
    <div
      className="relative size-[36px] shrink-0 rounded-full flex items-center justify-center group-hover:scale-105 transition-transform duration-300"
      style={{
        background: `conic-gradient(#e6202f 0deg ${deg}deg, #fcdbd8 ${deg}deg 360deg)`,
      }}
      title={`${pct}% AI Match`}
    >
      <div className="absolute size-[28px] rounded-full bg-white flex items-center justify-center shadow-inner">
        <span className="text-[9px] font-bold text-on-surface">{pct}%</span>
      </div>
    </div>
  )
}

export type MergedSupplierItem =
  | {
      id: string
      type: "internal"
      name: string
      location: string
      description?: string
      website?: string
      email?: string
      phoneNumber?: string
      rank?: number
      verificationStatus?: string
      tags?: string[]
      yearEstablished?: number
      annualRevenue?: string
      companyType?: string
      original: CompanySearchResult
    }
  | {
      id: string
      type: "lead"
      name: string
      location: string
      description?: string
      website?: string
      email?: string
      phoneNumber?: string
      rank?: number
      verificationStatus?: string
      tags?: string[]
      companyType?: string
      yearEstablished?: number
      annualRevenue?: string
      original: LeadSeller
    }

function ProductImageCarousel({ images, productName }: { images?: ProductImage[]; productName: string }) {
  const [currentIndex, setCurrentIndex] = useState(0)

  if (!images || images.length === 0) {
    return (
      <div className="w-full sm:w-44 sm:h-44 md:w-48 md:h-48 h-56 bg-[#fff0ef] border-[0.5px] border-[#e7bdb9]/30 rounded-xl flex items-center justify-center text-[#be001e] shrink-0 font-sans shadow-inner">
        <Package className="size-12 opacity-40" />
      </div>
    )
  }

  if (images.length === 1) {
    return (
      <div className="w-full sm:w-44 sm:h-44 md:w-48 md:h-48 h-56 rounded-xl overflow-hidden border border-slate-200 shrink-0 bg-slate-50 shadow-sm relative group/single">
        <img
          src={images[0].url}
          alt={productName}
          className="w-full h-full object-cover group-hover/single:scale-105 transition-transform duration-200"
        />
        {(images[0].caption || (images[0].order !== undefined && images[0].order !== null)) && (
          <div className="absolute bottom-0 inset-x-0 bg-black/75 text-white text-[9px] px-2 py-1 flex items-center justify-between gap-1 font-sans font-semibold">
            <span className="truncate">{images[0].caption || "Image 1"}</span>
            {images[0].order !== undefined && images[0].order !== null && (
              <span className="shrink-0 bg-white/20 px-1 rounded text-[8px] font-mono">Order: {images[0].order}</span>
            )}
          </div>
        )}
      </div>
    )
  }

  const currentImage = images[currentIndex] || images[0]

  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation()
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
  }

  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation()
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
  }

  return (
    <div className="relative group/carousel shrink-0 w-full sm:w-44 sm:h-44 md:w-48 md:h-48 h-56 rounded-xl overflow-hidden border border-slate-200 bg-slate-900 shadow-md flex flex-col justify-between select-none">
      <img
        src={currentImage.url}
        alt={currentImage.caption || productName}
        className="w-full h-full object-cover transition-all duration-300"
      />

      {/* Navigation Overlay Arrows */}
      <button
        type="button"
        onClick={prevImage}
        className="absolute left-1.5 top-1/2 -translate-y-1/2 p-1 rounded-full bg-black/60 hover:bg-black/80 text-white transition-all cursor-pointer opacity-90 hover:opacity-100 shadow z-10"
        title="Previous Image"
      >
        <ChevronLeft className="size-4" />
      </button>

      <button
        type="button"
        onClick={nextImage}
        className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded-full bg-black/60 hover:bg-black/80 text-white transition-all cursor-pointer opacity-90 hover:opacity-100 shadow z-10"
        title="Next Image"
      >
        <ChevronRight className="size-4" />
      </button>

      {/* Caption & Indicator Badge */}
      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-2 pt-4 flex items-end justify-between gap-1 text-white pointer-events-none z-10">
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-[10px] font-semibold truncate leading-tight">
            {currentImage.caption || `Image ${currentIndex + 1}`}
          </span>
        </div>
        <span className="text-[9px] font-mono bg-black/60 px-1.5 py-0.5 rounded-md shrink-0 font-bold self-end">
          {currentIndex + 1}/{images.length}
        </span>
      </div>

      {/* Slide dots */}
      <div className="absolute top-2 inset-x-0 flex justify-center gap-1 z-10">
        {images.map((_, idx) => (
          <button
            key={idx}
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setCurrentIndex(idx)
            }}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              idx === currentIndex ? "w-4 bg-white" : "w-1.5 bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
    </div>
  )
}

// ── Product Details View ──────────────────────────────────────────────────────
export function ProductDetailsView({
  productId,
  highlightVariantId,
  onBack,
  companyName,
  onQuickInquiry,
  onVariantDetails,
}: {
  productId: string
  highlightVariantId?: string | null
  onBack: () => void
  companyName?: string
  onQuickInquiry: (product: any) => void
  onVariantDetails?: (variantId: string) => void
}) {
  const { data: product, isLoading, isError, error } = useProductDetails(productId)
  
  const searchParams = useSearch({ strict: false }) as any
  const navigate = useNavigate()
  const selectedVariantId = (searchParams as any).variantId || null

  const [variantRfqTarget, setVariantRfqTarget] = useState<any>(null)

  // Auto-scroll to highlighted variant when data is loaded
  useEffect(() => {
    if (product && highlightVariantId) {
      const targetId = (highlightVariantId === productId && (product.variants?.length ?? 0) > 0)
        ? product.variants?.[0]?.id
        : highlightVariantId
      const element = document.getElementById(`variant-${targetId}`)
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 100)
      }
    }
  }, [product, highlightVariantId, productId])

  // Show SubmitRfqPage if user clicks Request Quote from variant
  if (variantRfqTarget) {
    return (
      <SubmitRfqPage
        target={variantRfqTarget}
        onCancel={() => setVariantRfqTarget(null)}
        onSuccess={() => setVariantRfqTarget(null)}
      />
    )
  }

  if (isLoading) {
    return (
      <div className="relative rounded-xl border border-[#be001e]/10 bg-gradient-to-br from-[#be001e]/5 to-transparent p-12 flex flex-col items-center justify-center min-h-[300px]">
        <button
          type="button"
          onClick={onBack}
          className="absolute top-4 left-4 p-1.5 rounded-lg border border-outline-variant/30 bg-white text-secondary hover:bg-slate-50 transition cursor-pointer flex items-center justify-center"
          title="Back"
        >
          <ChevronLeft className="size-4" />
        </button>
        <Loader2 className="size-8 animate-spin text-[#be001e] mb-3" />
        <p className="text-xs text-secondary font-medium animate-pulse">Fetching product details...</p>
      </div>
    )
  }

  if (isError || !product) {
    return (
      <div className="relative rounded-xl border border-red-100 bg-red-50/50 p-6 flex flex-col items-center justify-center min-h-[300px] text-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="absolute top-4 left-4 p-1.5 rounded-lg border border-outline-variant/30 bg-white text-secondary hover:bg-slate-50 transition cursor-pointer flex items-center justify-center"
          title="Back"
        >
          <ChevronLeft className="size-4" />
        </button>
        <AlertCircle className="size-8 text-[#be001e]" />
        <p className="text-sm font-semibold text-[#be001e]">Failed to load product details</p>
        <p className="text-xs text-secondary max-w-sm">{(error as Error)?.message || "Please check your connection and try again."}</p>
        <button
          type="button"
          onClick={onBack}
          className="mt-2 rounded-xl border border-outline-variant/30 bg-white px-4 py-2 text-[12px] font-bold text-secondary hover:bg-surface-container-low transition active:scale-95 cursor-pointer"
        >
          Go Back
        </button>
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-[#be001e]/10 bg-gradient-to-br from-[#be001e]/5 to-transparent p-6 space-y-6 shadow-sm animate-in fade-in duration-200 text-left">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-outline-variant/30">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-xs font-bold text-[#be001e] hover:underline cursor-pointer"
        >
          <ArrowLeft className="size-4" /> Back to Products
        </button>
        <span className="text-[10px] font-black bg-[#be001e]/5 text-[#be001e] px-2.5 py-0.5 rounded border border-[#be001e]/15 tracking-wider uppercase">
          Product Details
        </span>
      </div>

      {/* Main Info */}
      <div className="flex flex-col sm:flex-row gap-5 md:gap-6 items-start">
        <ProductImageCarousel images={product.images} productName={product.productName} />
        
        <div className="flex-1 min-w-0 space-y-4 w-full">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h2 className="text-xl md:text-2xl font-black text-[#291716] tracking-tight mb-1 break-words">
                {product.productName}
              </h2>
              {companyName && (
                <p className="text-xs text-slate-500 mb-2 font-medium">by <span className="font-semibold">{companyName}</span></p>
              )}
              {product.description && (
                <p className="text-xs text-slate-500 leading-relaxed max-w-2xl font-normal">
                  {product.description}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
            {[
              { icon: Hash, label: "Product Code", value: product.productCode },
              { icon: Layers, label: "Category", value: product.productCategory },
              { icon: Layers, label: "Sub-Category", value: product.productSubCategory },
              { icon: Calendar, label: "Added", value: new Date(product.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) },
            ].map(({ icon: Icon, label, value }) =>
              value ? (
                <div key={label} className="bg-slate-50/70 border-[0.5px] border-slate-200/60 p-2.5 md:p-3 rounded-xl flex flex-col gap-1 min-w-0">
                  <div className="flex items-center gap-1 text-[#be001e] min-w-0">
                    <Icon className="size-3.5 shrink-0" />
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-[#5d3f3d] truncate">{label}</span>
                  </div>
                  <p className="text-xs font-bold text-[#291716] truncate" title={value}>{value}</p>
                </div>
              ) : null
            )}
          </div>

          {product.tags && product.tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 pt-2">
              <div className="flex items-center gap-1 text-[#5d3f3d] text-[10px] shrink-0">
                <Tag className="size-3.5 text-[#be001e]" />
                <span className="font-bold uppercase tracking-wider">Tags</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {product.tags.map((t) => (
                  <span key={t} className="px-2.5 py-0.5 bg-[#be001e]/5 border-[0.5px] border-[#be001e]/20 rounded-full text-[10px] font-semibold text-[#be001e]">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Variants Section */}
      <div className="border-t border-outline-variant/30 pt-6 space-y-4">
        <div className="flex items-center gap-2">
          <Layers className="size-4 text-[#be001e]" />
          <h4 className="text-xs font-black uppercase tracking-wider text-[#be001e]">Product Variants</h4>
        </div>

        {!product.variants || product.variants.length === 0 ? (
          <p className="text-xs text-secondary italic">No variants available for this product.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {product.variants.map((v, index) => {
              let formattedPrice = "Contact for Price"
              if (v.price !== undefined && v.price !== null) {
                try {
                  const currency = (v.currencyCode && v.currencyCode !== "string") ? v.currencyCode.toUpperCase() : "INR"
                  formattedPrice = new Intl.NumberFormat("en-IN", {
                    style: "currency",
                    currency,
                    maximumFractionDigits: 2,
                  }).format(v.price)
                } catch (e) {
                  formattedPrice = `${v.currencyCode && v.currencyCode !== "string" ? v.currencyCode.toUpperCase() : "INR"} ${v.price}`
                }
              }

              const isHighlighted = highlightVariantId === v.id || 
                (highlightVariantId === productId && index === 0)

                return (
                  <div 
                    key={v.id} 
                    id={`variant-${v.id}`}
                    className={cn(
                      "p-4 rounded-xl space-y-3 transition shadow-sm",
                      isHighlighted 
                        ? "border border-[#be001e] bg-[#be001e]/[0.03] shadow-[0_0_15px_rgba(190,0,30,0.15)] ring-1 ring-[#be001e]/30" 
                        : "border border-outline-variant/30 bg-white/40 hover:bg-white/60"
                    )}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h5 className="text-[13px] font-bold text-on-surface">{v.name || "Unnamed Variant"}</h5>
                        <span className="inline-block px-1.5 py-0.5 bg-white/95 border border-outline-variant/20 text-secondary text-[9px] font-mono rounded mt-1">
                          SKU: {v.sku}
                        </span>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-extrabold text-[#be001e]">{formattedPrice}</p>
                        {v.stockQuantity !== undefined && v.stockQuantity !== null && (
                          <p className="text-[9px] text-secondary font-bold uppercase mt-0.5">
                            Stock: <span className="text-on-surface font-black">{v.stockQuantity}</span>
                          </p>
                        )}
                      </div>
                    </div>

                    {v.description && (
                      <p className="text-[11px] text-secondary leading-normal line-clamp-2">{v.description}</p>
                    )}

                    <div className="flex justify-between items-center text-[9px] text-secondary pt-2 border-t border-outline-variant/20">
                      <span>HSN: <strong>{v.hsnCode || "N/A"}</strong></span>
                      {v.tags && v.tags.length > 0 && (
                        <span className="bg-[#be001e]/5 text-[#be001e] px-1.5 py-0.2 rounded border border-[#be001e]/10 font-bold uppercase text-[8px]">
                          {v.tags[0]}
                        </span>
                      )}
                    </div>

                    {/* Variant Action Buttons */}
                    <div className="flex gap-2 pt-2 border-t border-outline-variant/20">
                      <button
                        type="button"
                        onClick={() => {
                          if (onVariantDetails) {
                            onVariantDetails(v.id)
                          } else {
                            const source = searchParams?.source
                            const category = searchParams?.category
                            // Detect context: if we're inside the explore route tree,
                            // navigate to the explore variant route to preserve breadcrumb.
                            const isExploreCtx = window.location.pathname.includes("/explore")
                            const variantRoute = isExploreCtx
                              ? "/explore/products/$productId/variants/$variantId"
                              : "/browse-products/products/$productId/variants/$variantId"
                            navigate({
                              to: variantRoute as any,
                              params: { productId, variantId: v.id },
                              search: {
                                ...(companyName ? { companyName } : {}),
                                ...(source ? { source } : {}),
                                ...(category ? { category } : {}),
                                ...(product?.productName ? { productName: product.productName } : {}),
                                ...(v.name ? { variantName: v.name } : {}),
                              },
                            } as any)
                          }
                        }}
                        className="flex-1 flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 px-3 py-1.5 text-[11px] font-bold text-slate-700 transition active:scale-95 cursor-pointer"
                      >
                        <Package className="size-3" /> Details
                      </button>
                      <button
                        type="button"
                        onClick={() => setVariantRfqTarget({
                          type: "single-product",
                          product: { ...product, id: v.productId, productVariantId: v.id, productName: v.name || "Variant", name: v.name },
                          companyName,
                        })}
                        className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-[#be001e] hover:bg-[#be001e]/90 px-3 py-1.5 text-[11px] font-bold text-white transition active:scale-95 cursor-pointer"
                      >
                        <Sparkles className="size-3" /> Request Quote
                      </button>
                    </div>
                  </div>
                )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Product Variant Details View ──────────────────────────────────────────────
export function ProductVariantDetailsView({
  variantId,
  companyName,
  onBack,
  onRequestQuote,
}: {
  variantId: string
  companyName?: string
  onBack: () => void
  onRequestQuote: (variant: any) => void
}) {
  const { data: variant, isLoading, isError, error } = useProductVariantDetails(variantId)

  if (isLoading) {
    return (
      <div className="relative rounded-xl border border-[#be001e]/10 bg-gradient-to-br from-[#be001e]/5 to-transparent p-12 flex flex-col items-center justify-center min-h-[300px]">
        <button
          type="button"
          onClick={onBack}
          className="absolute top-4 left-4 p-1.5 rounded-lg border border-outline-variant/30 bg-white text-secondary hover:bg-slate-50 transition cursor-pointer flex items-center justify-center"
          title="Back"
        >
          <ChevronLeft className="size-4" />
        </button>
        <Loader2 className="size-8 animate-spin text-[#be001e] mb-3" />
        <p className="text-xs text-secondary font-medium animate-pulse">Fetching variant details...</p>
      </div>
    )
  }

  if (isError || !variant) {
    return (
      <div className="relative rounded-xl border border-red-100 bg-red-50/50 p-6 flex flex-col items-center justify-center min-h-[300px] text-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="absolute top-4 left-4 p-1.5 rounded-lg border border-outline-variant/30 bg-white text-secondary hover:bg-slate-50 transition cursor-pointer flex items-center justify-center"
          title="Back"
        >
          <ChevronLeft className="size-4" />
        </button>
        <AlertCircle className="size-8 text-[#be001e]" />
        <p className="text-sm font-semibold text-[#be001e]">Failed to load variant details</p>
        <p className="text-xs text-secondary max-w-sm">{(error as Error)?.message || "Please check your connection and try again."}</p>
        <button type="button" onClick={onBack} className="mt-2 rounded-xl border border-outline-variant/30 bg-white px-4 py-2 text-[12px] font-bold text-secondary hover:bg-surface-container-low transition active:scale-95 cursor-pointer">
          Go Back
        </button>
      </div>
    )
  }

  const prod = variant.product || {}

  let formattedPrice = "Contact for Price"
  if (variant.price !== undefined && variant.price !== null) {
    try {
      const currency = (variant.currencyCode && variant.currencyCode !== "string") ? variant.currencyCode.toUpperCase() : "INR"
      formattedPrice = new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 2 }).format(variant.price)
    } catch {
      formattedPrice = `${variant.currencyCode || "INR"} ${variant.price}`
    }
  }

  return (
    <div className="rounded-xl border border-[#be001e]/10 bg-gradient-to-br from-[#be001e]/5 to-transparent p-6 space-y-6 shadow-sm animate-in fade-in duration-200 text-left">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-outline-variant/30">
        <button type="button" onClick={onBack} className="flex items-center gap-1.5 text-xs font-bold text-[#be001e] hover:underline cursor-pointer">
          <ArrowLeft className="size-4" /> Back to Product
        </button>
        <span className="text-[10px] font-black bg-[#be001e]/5 text-[#be001e] px-2.5 py-0.5 rounded border border-[#be001e]/15 tracking-wider uppercase">
          Variant Details
        </span>
      </div>

      {/* Variant Title & Company (Full Width Top) */}
      <div>
        <h3 className="text-xl font-bold text-on-surface leading-tight">{variant.name || "Unnamed Variant"}</h3>
        {companyName && (
          <p className="text-xs text-secondary mt-1">by <span className="font-semibold text-slate-700">{companyName}</span></p>
        )}
      </div>

      {/* 2-Column Grid directly below variant.name */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Left Section (5 cols): Key-Value Specs & Metrics */}
        <div className="lg:col-span-5 space-y-5">
          {/* Price & Stock Tiles */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/40 border border-outline-variant/20 rounded-xl p-3.5 shadow-2xs">
              <p className="text-[10px] font-bold uppercase tracking-wider text-secondary mb-1">Price</p>
              <p className="text-base font-extrabold text-[#be001e]">{formattedPrice}</p>
            </div>
            <div className="bg-white/40 border border-outline-variant/20 rounded-xl p-3.5 shadow-2xs">
              <p className="text-[10px] font-bold uppercase tracking-wider text-secondary mb-1">Stock</p>
              <p className="text-base font-extrabold text-on-surface">
                {variant.stockQuantity !== null && variant.stockQuantity !== undefined ? variant.stockQuantity : "N/A"}
              </p>
            </div>
          </div>

          {/* Key Specifications Grid */}
          <div className="bg-white/40 border border-outline-variant/20 rounded-xl p-4 space-y-3 shadow-2xs">
            <h4 className="text-[10px] font-extrabold uppercase tracking-wider text-secondary opacity-80 border-b border-outline-variant/15 pb-2">
              Key Specifications
            </h4>
            <div className="space-y-2 text-xs w-fit">
              <div className="grid grid-cols-[110px_1fr] items-center py-0.5">
                <span className="text-secondary font-medium">SKU</span>
                <span className="font-semibold text-on-surface">{variant.sku || "N/A"}</span>
              </div>
              {variant.hsnCode && (
                <div className="grid grid-cols-[110px_1fr] items-center py-0.5 border-t border-outline-variant/10 pt-2">
                  <span className="text-secondary font-medium">HSN Code</span>
                  <span className="font-semibold text-on-surface">{variant.hsnCode}</span>
                </div>
              )}
              {prod.productCode && (
                <div className="grid grid-cols-[110px_1fr] items-center py-0.5 border-t border-outline-variant/10 pt-2">
                  <span className="text-secondary font-medium">Product Code</span>
                  <span className="font-semibold text-on-surface">{prod.productCode}</span>
                </div>
              )}
              {prod.productCategory && (
                <div className="grid grid-cols-[110px_1fr] items-center py-0.5 border-t border-outline-variant/10 pt-2">
                  <span className="text-secondary font-medium">Category</span>
                  <span className="font-semibold text-[#be001e]">{prod.productCategory}</span>
                </div>
              )}
              {prod.productSubCategory && (
                <div className="grid grid-cols-[110px_1fr] items-center py-0.5 border-t border-outline-variant/10 pt-2">
                  <span className="text-secondary font-medium">Sub Category</span>
                  <span className="font-semibold text-on-surface">{prod.productSubCategory}</span>
                </div>
              )}
              {variant.tags && variant.tags.length > 0 && (
                <div className="grid grid-cols-[110px_1fr] items-center py-0.5 border-t border-outline-variant/10 pt-2">
                  <span className="text-secondary font-medium">Tags</span>
                  <span className="font-semibold text-on-surface">{variant.tags.join(", ")}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Section (7 cols): Variant Description */}
        <div className="lg:col-span-7 space-y-5">
          <div className="pb-5 px-5 shadow-2xs space-y-2.5 h-full">
            <h4 className="text-xs font-extrabold uppercase tracking-wider text-[#be001e] flex items-center gap-1.5">
              <FileText className="size-4" /> Variant Description
            </h4>
            <p className="text-[13px] text-on-surface leading-relaxed">
              {variant.description || "No specific description provided for this variant."}
            </p>
          </div>
        </div>

      </div>

      {/* Parent Product Info */}
      {prod.productName && (
        <div className="border-t border-outline-variant/30 pt-5 space-y-3">
          <div className="flex items-center gap-2">
            <Package className="size-4 text-[#be001e]" />
            <h4 className="text-xs font-black uppercase tracking-wider text-[#be001e]">Parent Product</h4>
          </div>
          <div className="bg-white/40 border border-outline-variant/20 rounded-xl p-4 space-y-2">
            <div className="flex justify-between items-start flex-wrap gap-2">
              <div>
                <p className="text-[14px] font-bold text-on-surface">{prod.productName}</p>
                {companyName && <p className="text-[11px] text-secondary">by <span className="font-semibold">{companyName}</span></p>}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {prod.productCode && <span className="px-2 py-0.5 bg-white/80 text-secondary text-[9px] font-bold rounded border border-outline-variant/20 uppercase">Code: {prod.productCode}</span>}
                {prod.productSubCategory && <span className="px-2 py-0.5 bg-white/80 text-secondary text-[9px] font-bold rounded border border-outline-variant/20 uppercase">{prod.productSubCategory}</span>}
              </div>
            </div>
            {prod.description && <p className="text-[12px] text-secondary leading-relaxed">{prod.description}</p>}
          </div>
        </div>
      )}

      {/* Action Footer */}
      <div className="border-t border-outline-variant/30 pt-4 flex justify-center md:justify-end items-center md:pr-[6%] lg:pr-0">
        <button
          type="button"
          onClick={() => onRequestQuote(variant)}
          className="flex items-center gap-2 rounded-xl bg-[#be001e] px-6 py-2 text-[12px] font-bold text-white hover:bg-[#be001e]/90 transition active:scale-95 cursor-pointer"
        >
          Request Quote <Sparkles className="size-3.5" />
        </button>
      </div>
    </div>
  )
}

// ── Helper Components ─────────────────────────────────────────────────────────

function TruncatedText({ text, maxLength = 150 }: { text: any; maxLength?: number }) {
  const [expanded, setExpanded] = useState(false)
  if (!text) return null
  const str = typeof text === "string" ? text : String(text)
  if (str.length <= maxLength) return <>{str}</>

  const displayedText = expanded ? str : `${str.slice(0, maxLength)}...`

  return (
    <>
      {displayedText}
      <button 
        type="button" 
        onClick={() => setExpanded(!expanded)}
        className="text-[#be001e] font-semibold hover:underline ml-1 text-[11px]"
      >
        {expanded ? "less" : "more"}
      </button>
    </>
  )
}

function TruncatedList({ items, maxItems = 4 }: { items: string[]; maxItems?: number }) {
  const [expanded, setExpanded] = useState(false)
  if (!items || items.length === 0) return null
  if (items.length <= maxItems) return <>{items.map((item, i) => <span key={i} className="px-2.5 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-medium rounded-full border border-slate-200/65">{item}</span>)}</>

  const displayedItems = expanded ? items : items.slice(0, maxItems)

  return (
    <>
      {displayedItems.map((item, i) => (
        <span key={i} className="px-2.5 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-medium rounded-full border border-slate-200/65">
          {item}
        </span>
      ))}
      <button 
        type="button" 
        onClick={() => setExpanded(!expanded)}
        className="text-[#be001e] font-semibold hover:underline text-[10px] self-center ml-1"
      >
        {expanded ? "- less" : `+ ${items.length - maxItems} more`}
      </button>
    </>
  )
}

// ── Company Details View ──────────────────────────────────────────────────────
interface CompanyDetailsViewProps {
  companyId: string | null
  type: "internal" | "lead" | "external"
  onBack: () => void
  onRequestQuote: (companyOrSeller: any) => void
  leadSellers?: any[]
}

// Exported component
export function CompanyDetailsView({ companyId, type, onBack, onRequestQuote, leadSellers }: CompanyDetailsViewProps) {
  const isMobile = useIsMobile()
  const { user } = useAuthStore()

  const isInternal = type === "internal"
  const isLead = type === "lead"
  const isExternal = type === "external"
  const isExternalOrLead = isLead || isExternal

  if (!user && isExternalOrLead) {
    return (
      <PromptModalCard
        title="Log in to view supplier details"
        description="Create a free account or log in to view verified lead seller details, contact information, and business credentials."
        onCancel={onBack}
        onCancelText="Keep browsing"
      />
    )
  }

  const { data: company, isLoading: loadingInternal, isError: errorInternal } = useCompanyDetails(
    isInternal ? companyId : null,
    isInternal
  )
  const { data: leadSellerDetail, isLoading: loadingLead, isError: errorLead } = useExternalSeller(
    isExternalOrLead ? companyId : null,
    isExternalOrLead
  )

  const isLoading = isInternal ? loadingInternal : loadingLead
  const isError = isInternal ? errorInternal : errorLead

  if (isLoading) {
    return (
      <div className="relative rounded-xl border border-[#be001e]/10 bg-gradient-to-br from-[#be001e]/5 to-transparent p-12 flex flex-col items-center justify-center min-h-[300px]">
        <button
          type="button"
          onClick={onBack}
          className="absolute top-4 left-4 flex items-center justify-center p-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer text-slate-800"
        >
          <ChevronLeft className="size-4" />
        </button>
        <Loader2 className="size-8 animate-spin text-[#be001e] mb-3" />
        <p className="text-xs text-secondary font-medium animate-pulse">Fetching company profile...</p>
      </div>
    )
  }

  if (isError || (!company && isInternal) || (!leadSellerDetail && isExternalOrLead)) {
    return (
      <div className="relative rounded-xl border border-red-100 bg-red-50/50 p-6 flex flex-col items-center justify-center min-h-[300px] text-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="absolute top-4 left-4 flex items-center justify-center p-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer text-slate-800"
        >
          <ChevronLeft className="size-4" />
        </button>
        <AlertCircle className="size-8 text-[#be001e]" />
        <p className="text-sm font-semibold text-[#be001e]">Failed to load company details</p>
      </div>
    )
  }

  const leadProfileData = leadSellerDetail ? {
    name: leadSellerDetail.name,
    description: leadSellerDetail.description,
    city: leadSellerDetail.city,
    state: leadSellerDetail.state,
    addressLine1: leadSellerDetail.addressLine1,
    website: leadSellerDetail.website,
    companyType: leadSellerDetail.companyType,
    yearEstablished: leadSellerDetail.yearEstablished,
    annualRevenue: leadSellerDetail.annualRevenue
  } : {}

  const name = isInternal ? company?.name : leadSellerDetail?.name
  const description = isInternal ? company?.description : leadSellerDetail?.description
  const tags = isInternal ? company?.tags : undefined
  const city = isInternal ? company?.city : leadSellerDetail?.city
  const state = isInternal ? company?.state : leadSellerDetail?.state
  const country = isInternal ? company?.country : undefined
  const website = isInternal ? company?.website : undefined
  const email = isInternal ? company?.email : undefined
  const phoneNumber = isInternal ? company?.phoneNumber : undefined
  const industry = isInternal ? company?.industry : undefined
  const companyType = isInternal ? company?.companyType : leadSellerDetail?.companyType
  const annualRevenue = isInternal ? company?.annualRevenue : leadSellerDetail?.annualRevenue
  const employeeCount = isInternal ? company?.employeeCount : undefined
  const address = isInternal
    ? [company?.addressLine1, company?.addressLine2, company?.city, company?.state, company?.postalCode].filter(Boolean).join(", ")
    : leadSellerDetail?.addressLine1
  const taxId = isInternal ? company?.taxId : undefined
  const tradeAbility = isInternal ? company?.tradeAbility : undefined

  // Unified fields mapping (Internal + Lead)
  const c = isInternal ? (company || {}) : leadProfileData
  
  const iecCode = (c as any).iecCode
  const cinNumber = (c as any).cinNumber
  const dinNumber = (c as any).dinNumber
  const panNumber = (c as any).panNumber
  const legalStatus = (c as any).legalStatus
  const legalName = isInternal ? company?.legalName : undefined
  const acceptedPaymentCurrency = Array.isArray((c as any).acceptedPaymentCurrency) ? (c as any).acceptedPaymentCurrency.join(", ") : (c as any).acceptedPaymentCurrency
  const externalVerificationStatus = isInternal ? company?.verificationStatus : (c as any).externalVerificationStatus
  const verification = (c as any).verification
  
  const rating = isInternal ? undefined : (c as any).rating
  const reviewCount = isInternal ? undefined : (c as any).reviewCount
  const yearEstablished = (c as any).yearEstablished

  const awards = Array.isArray((c as any).awards) ? (c as any).awards.join(", ") : (c as any).awards
  const responseRate = (c as any).responseRate
  const responseTime = (c as any).responseTime
  const deliveryTime = (c as any).deliveryTime
  
  const tradeAssurance = (c as any).tradeAssurance
  const exportPercentage = (c as any).exportPercentage
  const exportMarkets = Array.isArray((c as any).exportMarkets) ? (c as any).exportMarkets.join(", ") : (c as any).exportMarkets
  const importMarkets = Array.isArray((c as any).importMarkets) ? (c as any).importMarkets.join(", ") : (c as any).importMarkets
  const shippingMethods = Array.isArray((c as any).shippingMethods) ? (c as any).shippingMethods.join(", ") : (c as any).shippingMethods
  const minimumOrderQuantity = (c as any).minimumOrderQuantity || (c as any).moq
  const sampleAvailability = (c as any).sampleAvailability
  const productionCapacity = (c as any).productionCapacity
  const customizationAvailable = (c as any).customizationAvailable
  
  const packagingDetails = (c as any).packagingDetails
  const acceptedDeliveryTerms = Array.isArray((c as any).acceptedDeliveryTerms) ? (c as any).acceptedDeliveryTerms.join(", ") : (c as any).acceptedDeliveryTerms
  const paymentTerms = Array.isArray((c as any).paymentTerms) ? (c as any).paymentTerms.join(", ") : (c as any).paymentTerms
  const acceptedPaymentMethods = Array.isArray((c as any).acceptedPaymentMethods) ? (c as any).acceptedPaymentMethods.join(", ") : (c as any).acceptedPaymentMethods
  
  const warehouseArea = (c as any).warehouseArea
  const certifications = Array.isArray((c as any).certifications) ? (c as any).certifications.join(", ") : (c as any).certifications
  const infrastructure = Array.isArray((c as any).infrastructure) ? (c as any).infrastructure.join(", ") : (c as any).infrastructure
  
  const twitterUrl = (c as any).twitterUrl
  const youtubeUrl = (c as any).youtubeUrl
  const facebookUrl = (c as any).facebookUrl
  const linkedinUrl = isInternal ? (company as any)?.linkedinUrl : (c as any).linkedinUrl
  const instagramUrl = (c as any).instagramUrl
  
  const keyPeople = (c as any).keyPeople
  const bankDetails = (c as any).bankDetails
  const memberships = Array.isArray((c as any).memberships) ? (c as any).memberships : typeof (c as any).memberships === 'string' ? [(c as any).memberships] : []
  const natureOfBusiness = Array.isArray((c as any).natureOfBusiness) ? (c as any).natureOfBusiness : typeof (c as any).natureOfBusiness === 'string' ? [(c as any).natureOfBusiness] : []

  const initials = name ? name.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase() : "CO"

  return (
    <div className={`animate-in fade-in duration-300 text-left w-full ${isMobile ? "space-y-3" : "space-y-6"}`}>
      {/* Profile Header Card */}
      <section className={`bg-white border border-slate-200 rounded-xl p-6 md:p-8 flex flex-col md:flex-row gap-6 md:gap-8 items-start relative overflow-hidden shadow-sm ${isMobile ? "mt-0" : "mt-4"}`}>
        <div className="absolute top-0 right-0 w-32 h-32 bg-[#be001e]/5 rounded-bl-full -mr-10 -mt-10 pointer-events-none"></div>
        
        {/* Logo Avatar */}
        <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-tr from-[#be001e] to-[#e60024] text-white flex items-center justify-center text-xl md:text-2xl font-black ring-4 ring-[#be001e]/10 shrink-0 shadow-md">
          {initials}
        </div>

        {/* Info */}
        <div className="flex-1 space-y-3.5 w-full">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="font-headline-md text-[22px] md:text-[26px] text-slate-800 font-bold leading-tight">{name}</h2>
              {isInternal && company?.verificationStatus === "APPROVED" && (
                <span className="bg-[#be001e]/10 text-[#be001e] px-2.5 py-0.5 rounded-full text-[9.5px] font-bold tracking-wider uppercase border border-[#be001e]/20">
                  Global Partner
                </span>
              )}
            </div>
            
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-2 text-slate-500 text-xs font-medium">
              {(city || state || country) && (
                <span className="flex items-center gap-1.5">
                  <MapPin className="size-4 text-[#be001e]" />
                  {[city, state, country].filter(Boolean).join(", ")}
                </span>
              )}
              {yearEstablished && (
                <span className="flex items-center gap-1.5">
                  <Calendar className="size-4 text-[#be001e]" />
                  Est. {yearEstablished}
                </span>
              )}
              {rating && (
                <span className="flex items-center gap-1.5">
                  <Star className="size-4 text-yellow-500 fill-yellow-500" />
                  <b className="text-slate-700">{rating}</b> {reviewCount ? `· ${reviewCount} reviews` : ""}
                </span>
              )}
            </div>

            {/* Quick Metadata Badges */}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              {industry && (
                <span className="px-2.5 py-0.5 bg-slate-100 border border-slate-200 text-slate-600 rounded-md text-[10.5px] font-bold">
                  {industry}
                </span>
              )}
              {companyType && (
                <span className="px-2.5 py-0.5 bg-slate-100 border border-slate-200 text-slate-600 rounded-md text-[10.5px] font-bold">
                  {companyType}
                </span>
              )}
              {exportPercentage && (
                <span className="px-2.5 py-0.5 bg-red-50 border border-red-100 text-[#be001e] rounded-md text-[10.5px] font-bold">
                  Export: {exportPercentage}
                </span>
              )}
              {certifications && (
                <span className="px-2.5 py-0.5 bg-blue-50 border border-blue-100 text-blue-700 rounded-md text-[10.5px] font-bold">
                  {certifications.split(",")[0].trim()} {certifications.split(",").length > 1 ? `+${certifications.split(",").length - 1}` : ""}
                </span>
              )}
            </div>
          </div>

          {/* Socials / Sharing icons */}
          <div className="flex gap-2">
            {website && (
              <a
                href={website.startsWith("http") ? website : `https://${website}`}
                target="_blank" rel="noreferrer"
                className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center hover:bg-[#be001e]/5 hover:border-[#be001e]/30 hover:text-[#be001e] transition-all text-slate-500"
                title="Website"
              >
                <Globe className="size-4" />
              </a>
            )}
            {email && (
              <a
                href={`mailto:${email}`}
                className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center hover:bg-[#be001e]/5 hover:border-[#be001e]/30 hover:text-[#be001e] transition-all text-slate-500"
                title="Email Address"
              >
                <Mail className="size-4" />
              </a>
            )}
            {linkedinUrl && (
              <a
                href={linkedinUrl}
                target="_blank" rel="noreferrer"
                className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center hover:bg-blue-50 hover:border-blue-300 hover:text-[#0a66c2] transition-all text-slate-500 font-bold text-xs"
                title="LinkedIn"
              >
                in
              </a>
            )}
            {twitterUrl && (
              <a
                href={twitterUrl}
                target="_blank" rel="noreferrer"
                className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 hover:text-slate-800 transition-all text-slate-500 font-bold text-xs"
                title="Twitter"
              >
                tw
              </a>
            )}
            {facebookUrl && (
              <a
                href={facebookUrl.startsWith("http") ? facebookUrl : `https://${facebookUrl}`}
                target="_blank" rel="noreferrer"
                className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 transition-all text-slate-500"
                title="Facebook"
              >
                <i className="fa-brands fa-facebook-f text-[13px]"></i>
              </a>
            )}
            {instagramUrl && (
              <a
                href={instagramUrl.startsWith("http") ? instagramUrl : `https://${instagramUrl}`}
                target="_blank" rel="noreferrer"
                className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center hover:bg-pink-50 hover:border-pink-300 hover:text-pink-600 transition-all text-slate-500"
                title="Instagram"
              >
                <i className="fa-brands fa-instagram text-[14px]"></i>
              </a>
            )}
            {youtubeUrl && (
              <a
                href={youtubeUrl.startsWith("http") ? youtubeUrl : `https://${youtubeUrl}`}
                target="_blank" rel="noreferrer"
                className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center hover:bg-red-50 hover:border-red-300 hover:text-red-650 transition-all text-slate-500"
                title="YouTube"
              >
                <i className="fa-brands fa-youtube text-[13px]"></i>
              </a>
            )}
          </div>
        </div>

        {/* Header Actions */}
        <div className="flex flex-col gap-3 w-full md:w-auto shrink-0 pt-2 md:pt-0 items-start md:items-end justify-center md:self-center">
          {/* Quick Response Stats */}
          {(responseRate || responseTime) && (
            <div className="text-left md:text-right space-y-0.5">
              {responseRate && (
                <p className="text-[11px] text-slate-500 font-semibold">
                  Response Rate: <span className="text-[#be001e] font-black">{responseRate}</span>
                </p>
              )}
              {responseTime && (
                <p className="text-[11px] text-slate-500 font-semibold">
                  Reply Time: <span className="text-slate-800 font-black">{responseTime}</span>
                </p>
              )}
            </div>
          )}

          <button
            onClick={() => onRequestQuote(isInternal ? company : leadSellerDetail)}
            className="bg-gradient-to-r from-[#be001e] to-[#e60024] text-white px-6 py-2.5 rounded-lg font-bold flex items-center justify-center gap-2 hover:brightness-110 active:scale-[0.98] transition-all shadow-md shadow-[#be001e]/15 cursor-pointer text-xs w-full md:w-auto"
          >
            <Send className="size-4" />
            Request Quote
          </button>
        </div>
      </section>


      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* About Section */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 md:p-6 space-y-5 shadow-sm" id="about">
            <h3 className="text-base font-bold text-slate-800 flex items-center gap-2 pb-3.5 border-b border-slate-100">
              <Building2 className="size-4.5 text-[#be001e]" />
              <span>About Company</span>
            </h3>
            {legalName && (
              <p className="text-[11px] text-slate-450 font-bold uppercase tracking-wider block mt-1.5">
                Legal Name: <span className="text-slate-650 font-semibold normal-case ml-1">{legalName}</span>
              </p>
            )}
            {description && (
              <p className="text-[13px] text-slate-600 leading-relaxed font-normal mt-2.5">
                <TruncatedText text={description} maxLength={180} />
              </p>
            )}

            {/* Nature of Business & Memberships Badges */}
            {((natureOfBusiness && natureOfBusiness.length > 0) || (memberships && memberships.length > 0)) && (
              <div className="flex flex-wrap gap-2.5 pt-2">
                {natureOfBusiness?.map((nob: string, idx: number) => (
                  <span key={idx} className="bg-slate-50 text-slate-600 border border-slate-200 px-2.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider">
                    💼 {nob}
                  </span>
                ))}
                {memberships?.map((member: string, idx: number) => (
                  <span key={idx} className="bg-[#be001e]/5 text-[#be001e] border border-[#be001e]/20 px-2.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider">
                    🏆 {member}
                  </span>
                ))}
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-5 gap-x-6 pt-3">
              {email && (
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Email Address</label>
                  <div className="flex items-center justify-between group bg-slate-50 border border-slate-100 px-3 py-1.5 rounded-lg">
                    <span className="text-xs font-semibold text-slate-700 truncate">{email}</span>
                  </div>
                </div>
              )}
              {phoneNumber && (
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Phone Number</label>
                  <div className="flex items-center justify-between group bg-slate-50 border border-slate-100 px-3 py-1.5 rounded-lg">
                    <span className="text-xs font-semibold text-slate-700 truncate">{phoneNumber}</span>
                  </div>
                </div>
              )}
              {industry && (
                <div className="space-y-0.5">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Industry</label>
                  <span className="text-xs font-semibold text-slate-700 block mt-1">{industry}</span>
                </div>
              )}
              {companyType && (
                <div className="space-y-0.5">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Company Type</label>
                  <span className="text-xs font-semibold text-slate-700 block mt-1">{companyType}</span>
                </div>
              )}
              {legalStatus && (
                <div className="space-y-0.5">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Legal Status</label>
                  <span className="text-xs font-semibold text-slate-700 block mt-1">{legalStatus}</span>
                </div>
              )}
              {annualRevenue && (
                <div className="space-y-0.5">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Annual Revenue</label>
                  <span className="text-xs font-semibold text-slate-700 block mt-1">{annualRevenue}</span>
                </div>
              )}
              {employeeCount && (
                <div className="space-y-0.5">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Employee Count</label>
                  <span className="text-xs font-semibold text-slate-700 block mt-1">{employeeCount}</span>
                </div>
              )}
              {address && (
                <div className="space-y-0.5 md:col-span-2">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Registered Address</label>
                  <span className="text-xs font-semibold text-slate-700 block mt-1">{address}</span>
                </div>
              )}
            </div>
          </div>

          {/* Trade & Capabilities Section */}
          {(tradeAbility || exportPercentage || exportMarkets || minimumOrderQuantity || productionCapacity || (customizationAvailable !== undefined && customizationAvailable !== null) || importMarkets || sampleAvailability || acceptedDeliveryTerms || acceptedPaymentMethods || paymentTerms || acceptedPaymentCurrency) && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 md:p-6 space-y-5 shadow-sm" id="trade">
              <h3 className="text-base font-bold text-slate-800 flex items-center gap-2 pb-3.5 border-b border-slate-100">
                <Layers className="size-4.5 text-[#be001e]" />
                <span>Trade &amp; Capabilities</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {tradeAbility && (
                  <div className="p-3.5 bg-slate-50 border border-slate-100 rounded-lg">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Trade Ability</span>
                    <span className="text-xs font-bold text-slate-700">{tradeAbility}</span>
                  </div>
                )}
                
                {exportPercentage && (
                  <div className="p-3.5 bg-slate-50 border border-slate-100 rounded-lg">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">Export %</span>
                    <div className="flex items-center gap-2.5">
                      <span className="text-xs font-bold text-slate-700">{exportPercentage}</span>
                      <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#be001e]"
                          style={{ width: `${parseInt(exportPercentage) || 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                )}
                
                {exportMarkets && (
                  <div className="p-3.5 bg-slate-50 border border-slate-100 rounded-lg">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Export Markets</span>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {exportMarkets.split(",").slice(0, 2).map((m: string, i: number) => (
                        <span key={i} className="text-[9.5px] font-bold bg-white border border-slate-200/80 px-2 py-0.5 rounded text-slate-600">{m.trim()}</span>
                      ))}
                    </div>
                  </div>
                )}

                {minimumOrderQuantity && (
                  <div className="p-3.5 bg-white border border-slate-200/80 rounded-lg">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Minimum Order Qty (MOQ)</span>
                    <span className="text-xs font-bold text-slate-700">{minimumOrderQuantity}</span>
                  </div>
                )}

                {productionCapacity && (
                  <div className="p-3.5 bg-white border border-slate-200/80 rounded-lg">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Production Capacity</span>
                    <span className="text-xs font-bold text-slate-700">{productionCapacity}</span>
                  </div>
                )}

                {customizationAvailable !== undefined && customizationAvailable !== null && (
                  <div className="p-3.5 bg-white border border-slate-200/80 rounded-lg">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Customization</span>
                    {customizationAvailable === true ? (
                      <div className="flex items-center gap-1.5 text-emerald-600 font-bold text-xs">
                        <CheckCircle2 className="size-4" />
                        <span>Available</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 text-slate-500 font-bold text-xs">
                        <span>Not Available</span>
                      </div>
                    )}
                  </div>
                )}

                {importMarkets && (
                  <div className="p-3.5 bg-slate-50 border border-slate-100 rounded-lg">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Import Markets</span>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {importMarkets.split(",").slice(0, 2).map((m: string, i: number) => (
                        <span key={i} className="text-[9.5px] font-bold bg-white border border-slate-200/80 px-2 py-0.5 rounded text-slate-600">{m.trim()}</span>
                      ))}
                    </div>
                  </div>
                )}

                {sampleAvailability && (
                  <div className="p-3.5 bg-slate-50 border border-slate-100 rounded-lg">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Sample Availability</span>
                    <span className="text-xs font-bold text-slate-700">{sampleAvailability}</span>
                  </div>
                )}

                {acceptedDeliveryTerms && (
                  <div className="p-3.5 bg-slate-50 border border-slate-100 rounded-lg">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Accepted Delivery Terms</span>
                    <span className="text-xs font-bold text-slate-700">{acceptedDeliveryTerms}</span>
                  </div>
                )}

                {acceptedPaymentMethods && (
                  <div className="p-3.5 bg-slate-50 border border-slate-100 rounded-lg">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Payment Methods</span>
                    <span className="text-xs font-bold text-slate-700">{acceptedPaymentMethods}</span>
                  </div>
                )}

                {paymentTerms && (
                  <div className="p-3.5 bg-slate-50 border border-slate-100 rounded-lg">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Payment Terms</span>
                    <span className="text-xs font-bold text-slate-700">{paymentTerms}</span>
                  </div>
                )}

                {acceptedPaymentCurrency && (
                  <div className="p-3.5 bg-slate-50 border border-slate-100 rounded-lg">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Accepted Currencies</span>
                    <span className="text-xs font-bold text-slate-700">{acceptedPaymentCurrency}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Operations & Quality */}
          {(warehouseArea || deliveryTime || responseRate || responseTime || tradeAssurance || shippingMethods || packagingDetails || infrastructure || certifications) && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 md:p-6 space-y-5 shadow-sm" id="operations">
              <h3 className="text-base font-bold text-slate-800 flex items-center gap-2 pb-3.5 border-b border-slate-100">
                <Zap className="size-4.5 text-[#be001e]" />
                <span>Operations &amp; Quality</span>
              </h3>
              
              {(warehouseArea || deliveryTime || responseRate || responseTime) && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5">
                  {warehouseArea && (
                    <div className="flex flex-col items-center p-3.5 border border-slate-150 rounded-xl text-center bg-slate-50/50">
                      <Building2 className="size-5.5 text-slate-400 mb-1.5" />
                      <span className="text-sm font-bold text-slate-700">{warehouseArea}</span>
                      <span className="text-[9px] text-slate-400 uppercase font-bold tracking-tight mt-0.5">Warehouse Sq Ft</span>
                    </div>
                  )}
                  {deliveryTime && (
                    <div className="flex flex-col items-center p-3.5 border border-slate-150 rounded-xl text-center bg-slate-50/50">
                      <Clock className="size-5.5 text-slate-400 mb-1.5" />
                      <span className="text-sm font-bold text-slate-700">{deliveryTime}</span>
                      <span className="text-[9px] text-slate-400 uppercase font-bold tracking-tight mt-0.5">Lead Time Days</span>
                    </div>
                  )}
                  {responseRate && (
                    <div className="flex flex-col items-center p-3.5 border border-slate-150 rounded-xl text-center bg-slate-50/50">
                      <TrendingUp className="size-5.5 text-slate-400 mb-1.5" />
                      <span className="text-sm font-bold text-slate-700">{responseRate}</span>
                      <span className="text-[9px] text-slate-400 uppercase font-bold tracking-tight mt-0.5">Response Rate</span>
                    </div>
                  )}
                  {responseTime && (
                    <div className="flex flex-col items-center p-3.5 border border-slate-150 rounded-xl text-center bg-slate-50/50">
                      <Clock className="size-5.5 text-slate-400 mb-1.5" />
                      <span className="text-sm font-bold text-slate-700">{responseTime}</span>
                      <span className="text-[9px] text-slate-400 uppercase font-bold tracking-tight mt-0.5">Reply Time</span>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-3 pt-1">
                {tradeAssurance && (
                  <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-100/60">
                    <Shield className="size-4.5 text-emerald-600 fill-emerald-600/10 shrink-0" />
                    <span className="text-[11px] font-semibold text-emerald-800">
                      Trade Assurance Protected: {tradeAssurance === true ? "100% On-time delivery & quality protection." : String(tradeAssurance)}
                    </span>
                  </div>
                )}
                {shippingMethods && (
                  <div className="p-3.5 bg-slate-50 border border-slate-100 rounded-lg">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Shipping Methods</span>
                    <span className="text-xs font-bold text-slate-700">{shippingMethods}</span>
                  </div>
                )}
                {packagingDetails && (
                  <div className="p-3.5 bg-slate-50 border border-slate-100 rounded-lg">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Packaging Details</span>
                    <span className="text-xs font-bold text-slate-700">{packagingDetails}</span>
                  </div>
                )}
                {infrastructure && (
                  <div className="p-3.5 bg-slate-50 border border-slate-100 rounded-lg">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Infrastructure / R&amp;D</span>
                    <span className="text-xs font-bold text-slate-700">{infrastructure}</span>
                  </div>
                )}
                {certifications && (
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Certifications</label>
                    <div className="flex flex-wrap gap-1.5">
                      {certifications.split(",").map((cert: string, idx: number) => (
                        <span key={idx} className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-[10.5px] flex items-center gap-1.5 font-medium border border-slate-200">
                          <Award className="size-3.5 text-[#be001e]" />
                          {cert.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}


        </div>

        {/* Right Column - Compliance & sidebar details */}
        <div className="space-y-6" id="compliance">
          
          {/* Compliance Card */}
          {(iecCode || cinNumber || dinNumber || panNumber || taxId || bankDetails) && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-5 shadow-sm">
              <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                <h4 className="font-bold text-sm text-slate-800">Compliance &amp; Regs</h4>
                {externalVerificationStatus && (
                  <span className="bg-emerald-100 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded text-[9.5px] font-black uppercase tracking-wider">
                    {externalVerificationStatus}
                  </span>
                )}
              </div>
              
              <div className="space-y-4">
                {iecCode && (
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">IEC Number</label>
                    <div className="flex items-center justify-between bg-slate-50 border border-slate-100 px-3 py-1.5 rounded-lg">
                      <code className="text-xs font-mono text-black font-black">{iecCode}</code>
                    </div>
                  </div>
                )}
                {cinNumber && (
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">CIN Number</label>
                    <div className="flex items-center justify-between bg-slate-50 border border-slate-100 px-3 py-1.5 rounded-lg">
                      <code className="text-xs font-mono text-black font-black">{cinNumber}</code>
                    </div>
                  </div>
                )}
                {dinNumber && (
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">DIN Number</label>
                    <div className="flex items-center justify-between bg-slate-50 border border-slate-100 px-3 py-1.5 rounded-lg">
                      <code className="text-xs font-mono text-black font-black">{dinNumber}</code>
                    </div>
                  </div>
                )}
                {panNumber && (
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">PAN Number</label>
                    <div className="flex items-center justify-between bg-slate-50 border border-slate-100 px-3 py-1.5 rounded-lg">
                      <code className="text-xs font-mono text-black font-black">{panNumber}</code>
                    </div>
                  </div>
                )}
                {taxId && (
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">TAX / VAT ID</label>
                    <div className="flex items-center justify-between bg-slate-50 border border-slate-100 px-3 py-1.5 rounded-lg">
                      <code className="text-xs font-mono text-black font-black">{taxId}</code>
                    </div>
                  </div>
                )}
                
                {bankDetails && (
                  <div className="mt-4 p-3.5 bg-slate-50 rounded-xl border border-slate-100 flex items-start gap-3">
                    <Landmark className="size-5 text-slate-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-bold text-slate-700">Banking Partner</p>
                      <p className="text-[10.5px] text-black font-bold mt-0.5">
                        {typeof bankDetails === "object"
                          ? [bankDetails.bankName, bankDetails.branch, bankDetails.accountType].filter(Boolean).join(" · ")
                          : String(bankDetails)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Awards Card */}
          {awards && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 shadow-sm">
              <h4 className="font-bold text-sm text-slate-800 pb-3 border-b border-slate-100">Recognition</h4>
              <div className="space-y-2">
                {awards.split(",").map((award: string, idx: number) => (
                  <div key={idx} className="flex items-center gap-3 p-2.5 bg-amber-50/50 rounded-lg border border-amber-100/60">
                    <Award className="size-4.5 text-amber-500 fill-amber-500/10 shrink-0" />
                    <span className="text-xs font-medium text-amber-900 leading-snug">{award.trim()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Focus Areas/Product Tags */}
          {tags && tags.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 shadow-sm">
              <h4 className="font-bold text-sm text-slate-800 pb-3 border-b border-slate-100">Focus Areas</h4>
              <div className="flex flex-wrap gap-1.5">
                {tags.map((tag: string, idx: number) => (
                  <span key={idx} className="px-3 py-1 bg-slate-50 border border-slate-200 rounded-full text-[10.5px] font-bold text-black hover:border-[#be001e] hover:text-[#be001e] transition-colors cursor-pointer">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Key Personnel */}
          {keyPeople && keyPeople.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 shadow-sm" id="people">
              <h4 className="font-bold text-sm text-slate-800 pb-3 border-b border-slate-100 font-sans">Key Personnel</h4>
              <div className="space-y-3">
                {keyPeople.map((person: any, idx: number) => {
                  const pInitials = person.name ? person.name.split(" ").map((w: string) => w[0]).join("").toUpperCase() : "PE"
                  return (
                    <div key={idx} className="flex items-center gap-3.5 p-3 border border-slate-150 rounded-xl hover:bg-slate-50/50 transition-colors">
                      <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-650 border border-slate-200 shrink-0 text-xs">
                        {pInitials}
                      </div>
                      <div>
                        <p className="font-black text-xs text-black">{person.name}</p>
                        {(person.designation || person.role) && (
                          <p className="text-[10px] text-slate-900 font-semibold">{person.designation || person.role}</p>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Verified Documents */}
          {verification && verification.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 shadow-sm">
              <h4 className="font-bold text-sm text-slate-800 pb-3 border-b border-slate-100 font-sans">Verified Documents</h4>
              <div className="space-y-3">
                {verification.map((doc: any, idx: number) => (
                  <a
                    key={idx}
                    href={doc.documentUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-start gap-3 p-2.5 bg-slate-50 border border-slate-150 rounded-xl hover:bg-[#be001e]/5 hover:border-[#be001e]/20 transition-all group"
                  >
                    <div className="w-9 h-11 bg-rose-50 border border-rose-100 rounded-lg flex items-center justify-center shrink-0 text-[#be001e] text-lg font-bold">
                      📄
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-black text-black truncate group-hover:text-[#be001e] transition-colors">
                        {doc.documentType || "Verification Document"}
                      </p>
                      {doc.documentIdentifier && (
                        <p className="text-[10px] text-slate-900 font-mono font-bold mt-0.5">ID: {doc.documentIdentifier}</p>
                      )}
                      {doc.status && (
                        <span className={cn(
                          "inline-block mt-1 px-1.5 py-0.2 text-[9px] font-black rounded uppercase border",
                          doc.status === "APPROVED" 
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200" 
                            : "bg-amber-50 text-amber-700 border-amber-200"
                        )}>
                          {doc.status}
                        </span>
                      )}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  )
}


// ── Animated AI Progress Bar ──────────────────────────────────────────────────
function AIProgressBar() {
  return (
    <div className="bg-white border border-[#f1e0de] rounded-xl p-4 overflow-hidden">
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#be001e]/5 rounded-lg">
            <TrendingUp className="size-5 text-[#be001e]" />
          </div>
          <div>
            <p className="text-[15px] font-semibold text-on-surface">AI Analysis in Progress</p>
            <p className="text-[11px] text-secondary">Matching requirements with manufacturer capabilities...</p>
          </div>
        </div>
        <span className="text-[11px] text-[#be001e] font-bold tracking-wider">Searching...</span>
      </div>
      {/* Animated sliding bar */}
      <div className="h-[2px] w-full bg-[#fcdbd8] rounded-full relative overflow-hidden">
        <div
          className="absolute h-full w-[30%] bg-[#e6202f] rounded-full"
          style={{ animation: "slideBar 1.5s infinite ease-in-out" }}
        />
      </div>
      <style>{`
        @keyframes slideBar {
          0% { left: -30%; }
          100% { left: 100%; }
        }
      `}</style>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
type RfqTarget =
  | { type: "single-product"; product: ProductSearchResult; companyName?: string }
  | { type: "single-company"; company: CompanySearchResult }
  | { type: "single-lead"; seller: LeadSeller }
  | { type: "multi-company"; companies: CompanySearchResult[]; leadSellers?: LeadSeller[] }
  | null

const TRENDING = ["Organic Spices", "Sustainable Packaging", "Cotton Fabric", "Steel Pipes", "Textiles & Apparel"]

const COMPANY_FILTER_OPTIONS = {
  exportMarkets: ["India", "USA", "Europe", "Middle East", "Asia-Pacific", "Africa", "Global"],
  certifications: ["ISO 9001", "ISO 14001", "GMP", "FDA", "CE", "Halal", "Kosher", "FSSAI", "GST"],
  industry: ["Manufacturing", "Export", "Agriculture", "Textiles", "Electronics", "Pharmaceuticals", "Automotive", "Food & Beverage"],
  companyType: ["Manufacturer", "Trading Company", "Distributor", "Wholesaler", "Commercial", "Retailer"],
  location: ["India", "China", "USA", "UK", "UAE", "Germany", "Vietnam", "Bangladesh"]
}

const PRODUCT_FILTER_OPTIONS_BASE = {
  minMoq: [1, 50, 100, 500, 1000]
}

// ── Category Browser Panel (navigation-based) ────────────────────────────────
// Clicking a category navigates to the dedicated Browse Products page.
function CategoryBrowserPanel({ }: { onProductSelect?: (p: any) => void, companyMap?: Map<string, string> }) {
  const navigate = useNavigate()
  const { data: categories = [], isLoading: loadingCategories, isError, error, refetch } = useProductCategories()

  if (loadingCategories) {
    return (
      <div className="space-y-6 animate-in fade-in duration-300">
        <div className="flex items-center justify-between pl-2 sm:pl-0">
          <h2 className="text-[24px] font-semibold tracking-[-0.015em] text-on-surface">
            Browse by Category
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
            <div
              key={n}
              className="flex items-center justify-between p-4 rounded-xl border border-outline-variant/30 bg-white min-h-[58px] h-full animate-pulse"
            >
              <div className="h-4 bg-slate-200 rounded-sm w-3/4" />
              <ChevronRight className="size-4 text-slate-200 shrink-0" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="space-y-6 animate-in fade-in duration-300">
        <div className="flex items-center justify-between pl-2 sm:pl-0">
          <h2 className="text-[24px] font-semibold tracking-[-0.015em] text-on-surface">
            Browse by Category
          </h2>
        </div>
        <div className="flex flex-col items-center justify-center p-8 bg-white border border-red-100 rounded-2xl text-center space-y-3 shadow-2xs">
          <div className="size-10 rounded-full bg-red-50 flex items-center justify-center text-[#be001e]">
            <AlertCircle className="size-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-800">Failed to load categories</h3>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex items-center justify-between pl-2 sm:pl-0">
        <h2 className="text-[24px] font-semibold tracking-[-0.015em] text-on-surface">
          Browse by Category
        </h2>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => navigate({
              to: "/browse-products",
              search: { category: cat, source: "explore" }
            })}
            className="flex items-center justify-between p-4 rounded-xl border border-outline-variant/30 bg-white hover:border-[#be001e]/40 hover:bg-[#be001e]/5 hover:shadow-sm transition-all cursor-pointer group text-left h-full"
          >
            <span className="text-[13px] font-bold text-slate-700 group-hover:text-[#be001e] line-clamp-2">{cat}</span>
            <ChevronRight className="size-4 text-slate-400 group-hover:text-[#be001e] shrink-0" />
          </button>
        ))}
      </div>
    </div>
  )
}

interface FindSupplierPageProps {
  activeTab?: "companies" | "products"
}

export function FindSupplierPage({ activeTab = "companies" }: FindSupplierPageProps) {
  const {
    inputValue, setInputValue,
    setSearchQuery,
    isMultiSelectActive, setIsMultiSelectActive,
    selectedCompanyIds, setSelectedCompanyIds,
    activeCompanyRfqTarget, setActiveCompanyRfqTarget,
    companyQueryMessage, setCompanyQueryMessage,
    companyQuantity, setCompanyQuantity,
    activeProductRfqTarget, setActiveProductRfqTarget,
    productQueryMessage, setProductQueryMessage,
    productQuantity, setProductQuantity,
    companyFilters: companyFiltersStore,
    productFilters: productFiltersStore,
    setCompanyFilters: setCompanyFiltersStore,
    setProductFilters: setProductFiltersStore,
    hasSearchResults,
    setHasSearchResults,
  } = useFindSupplierStore()

  const scrolledPastHero = useBuyerUIStore((s) => s.scrolledPastHero)

  const [topbarHeight, setTopbarHeight] = useState(0)
  const [tabsHeight, setTabsHeight] = useState(0)
  const tabsRef = useRef<HTMLDivElement>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false)
  const filterPanelRef = useRef<HTMLDivElement>(null)
  const filterButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!isFilterPanelOpen) return

    const handleOutsideClick = (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement
      if (
        filterPanelRef.current && 
        !filterPanelRef.current.contains(target) && 
        filterButtonRef.current && 
        !filterButtonRef.current.contains(target)
      ) {
        setIsFilterPanelOpen(false)
      }
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsFilterPanelOpen(false)
      }
    }

    document.addEventListener("click", handleOutsideClick)
    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.removeEventListener("click", handleOutsideClick)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [isFilterPanelOpen])

  useEffect(() => {
    // Measure topbar if it is in the same scroll container.
    // If header is outside the scroll container 'main', the sticky position inside 'main' should stick to top: 0.
    const topbar = document.querySelector("header")
    const mainContainer = document.querySelector("main")
    if (topbar && mainContainer && mainContainer.contains(topbar)) {
      setTopbarHeight(topbar.getBoundingClientRect().height)
    } else {
      setTopbarHeight(0) // Topbar is fixed/static outside scroll container, so top inside main is 0
    }
  }, [])

  const searchParams = useSearch({ strict: false }) as any
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [showLeadAuthPrompt, setShowLeadAuthPrompt] = useState(false)
  const urlSearchTerm = (searchParams.searchTerm as string) || ""

  // Carousel refs & states
  const carouselRef = useRef<HTMLDivElement>(null)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(true)

  const checkScroll = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current
      setShowLeftArrow(scrollLeft > 10)
      setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 10)
    }
  }

  const supplierCarouselRef = useRef<HTMLDivElement>(null)
  const [showSupplierLeftArrow, setShowSupplierLeftArrow] = useState(false)
  const [showSupplierRightArrow, setShowSupplierRightArrow] = useState(true)

  const checkSupplierScroll = () => {
    if (supplierCarouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = supplierCarouselRef.current
      setShowSupplierLeftArrow(scrollLeft > 10)
      setShowSupplierRightArrow(scrollLeft + clientWidth < scrollWidth - 10)
    }
  }

  // Queries & memoized lists for Categories, Suppliers and RFQs
  const { data: rawCategories = [], isLoading: loadingCategories, isError: isCategoriesError } = useProductCategories()
  const categories = useMemo(() => rawCategories || [], [rawCategories])

  const { data: rawCompanies = [], isLoading: loadingSuppliers, isError: isSuppliersError } = useGetCompanies()
  const companies = useMemo(() => rawCompanies || [], [rawCompanies])

  const { data: rfqsList } = useGetBuyerRfqs()

  const recentRfqs = useMemo(() => {
    return rfqsList
      ? [...rfqsList].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 3)
      : []
  }, [rfqsList])

  const recommendedSuppliers = useMemo(() => {
    const companiesList = companies || []
    if (!companiesList.length) return []
    const recentCategories = new Set<string>()
    recentRfqs.forEach(rfq => {
      rfq.targets?.forEach(target => {
        if (target.product?.productCategory) recentCategories.add(target.product.productCategory.toLowerCase())
      })
    })
    const targetedCompanyIds = new Set<string>()
    rfqsList?.forEach(rfq => {
      rfq.targets?.forEach(target => {
        if (target.companyId) targetedCompanyIds.add(target.companyId)
        if (target.company?.id) targetedCompanyIds.add(target.company.id)
      })
    })
    let filtered = companiesList.filter(c => !targetedCompanyIds.has(c.id))
    if (recentCategories.size > 0) {
      filtered = [...filtered].sort((a, b) => {
        const aMatch = (a.tags || []).some((t: string) => recentCategories.has(t.toLowerCase())) || (a.industry && recentCategories.has(a.industry.toLowerCase()))
        const bMatch = (b.tags || []).some((t: string) => recentCategories.has(t.toLowerCase())) || (b.industry && recentCategories.has(b.industry.toLowerCase()))
        if (aMatch && !bMatch) return -1
        if (!aMatch && bMatch) return 1
        return 0
      })
    }
    return filtered
  }, [companies, recentRfqs, rfqsList])

  useEffect(() => {
    checkScroll()
    const t = setTimeout(checkScroll, 100)
    window.addEventListener("resize", checkScroll)
    return () => { clearTimeout(t); window.removeEventListener("resize", checkScroll) }
  }, [categories])

  useEffect(() => {
    checkSupplierScroll()
    const t = setTimeout(checkSupplierScroll, 100)
    window.addEventListener("resize", checkSupplierScroll)
    return () => { clearTimeout(t); window.removeEventListener("resize", checkSupplierScroll) }
  }, [companies])

  const handleCategoryClick = (categoryName: string) => {
    navigate({
      to: "/browse-products",
      search: { category: categoryName, source: "explore" }
    })
  }

  const handleSupplierClick = (supplier: any) => {
    if (!user && (supplier.type === "lead" || supplier.type === "external")) {
      setShowLeadAuthPrompt(true)
      return
    }
    const rawId = supplier.type === "internal"
      ? supplier.id
      : supplier.id.replace("lead-", "")
    navigate({
      to: "/explore/companies/$companyId",
      params: { companyId: rawId },
      search: {
        companyType: (supplier.type || "internal") as "internal" | "lead",
        companyName: supplier.name || supplier.companyName || undefined,
      },
    })
  }

  useEffect(() => {
    if (tabsRef.current) {
      setTabsHeight(tabsRef.current.getBoundingClientRect().height)
    }
  }, [urlSearchTerm, activeTab, successMessage])

  const selectedDetailProductId = searchParams.productId || null
  const highlightVariantId = searchParams.highlightVariantId || null
  const selectedDetailCompany = searchParams.companyId ? {
    id: searchParams.companyId,
    type: searchParams.companyType || "internal",
    name: "Supplier" // Could be fetched or ignored
  } : null

  const handleClearDetails = () => {
    navigate({
      search: (prev: any) => ({
        ...prev,
        productId: undefined,
        companyId: undefined,
        companyType: undefined,
        highlightVariantId: undefined,
      }),
      replace: true,
    } as any)
  }

  const companyFilters = useMemo(() => {
    try {
      return searchParams.companyFilters ? JSON.parse(searchParams.companyFilters) : {}
    } catch {
      return {}
    }
  }, [searchParams.companyFilters])

  const productFilters = useMemo(() => {
    try {
      return searchParams.productFilters ? JSON.parse(searchParams.productFilters) : {}
    } catch {
      return {}
    }
  }, [searchParams.productFilters])

  const queryClient = useQueryClient()
  const { data, isLoading, isError, error, isFetching, refetch } = useSearchSuppliers(urlSearchTerm, companyFilters, productFilters)

  useEffect(() => {
    if (activeTab === "companies") {
      setCompanyFiltersStore(companyFilters)
    }
  }, [companyFilters, activeTab, setCompanyFiltersStore])

  useEffect(() => {
    if (activeTab === "products") {
      setProductFiltersStore(productFilters)
    }
  }, [productFilters, activeTab, setProductFiltersStore])

  // ── Search ↔ URL sync ───────────────────────────────────────────────────────
  // The URL `searchTerm` is the single source of truth for the committed search
  // on this page. Results (react-query) are keyed on the URL param directly, so
  // manual URL edits, share links, navigate_ui and back/forward all "just work"
  // with no ping-pong between effects. The store mirror is kept only so
  // downstream views (e.g. SubmitRfqPage) can read the active query.

  // Reflect an externally-changed URL into the search box + committed query.
  // Keyed on `urlSearchTerm` alone, so typing in the box never triggers it.
  useEffect(() => {
    setSearchQuery(urlSearchTerm)
    setInputValue(urlSearchTerm)
  }, [urlSearchTerm, setSearchQuery, setInputValue])

  // Commit a search by writing it to the URL and invalidating cache for fresh results.
  const commitSearch = (query: string) => {
    setSearchQuery(query)
    setInputValue(query)
    setIsMultiSelectActive(false)
    setSelectedCompanyIds([])
    setActiveCompanyRfqTarget(null)
    setActiveProductRfqTarget(null)
    setSuccessMessage(null)
    queryClient.invalidateQueries({ queryKey: ["search-suppliers"] })
    if (query === urlSearchTerm) {
      refetch()
    } else {
      navigate({
        search: (prev: any) => ({
          ...prev,
          searchTerm: query || undefined,
          productId: undefined,
          companyId: undefined,
          companyType: undefined,
          highlightVariantId: undefined,
        }),
      } as any)
    }
  }

  const setCompanyFilters = (updater: any) => {
    const nextFilters = typeof updater === "function" ? updater(companyFilters) : updater
    navigate({
      search: (prev: any) => ({
        ...prev,
        companyFilters: Object.keys(nextFilters).length > 0 ? JSON.stringify(nextFilters) : undefined,
      }),
      replace: true,
    } as any)
  }

  const setProductFilters = (updater: any) => {
    const nextFilters = typeof updater === "function" ? updater(productFilters) : updater
    navigate({
      search: (prev: any) => ({
        ...prev,
        productFilters: Object.keys(nextFilters).length > 0 ? JSON.stringify(nextFilters) : undefined,
      }),
      replace: true,
    } as any)
  }

  const hasActiveCompanyFilters = Object.values(companyFilters).some((val: any) => Array.isArray(val) && val.length > 0)
  const hasActiveProductFilters = Object.entries(productFilters).some(([key, val]: any) => {
    if (key === "category") return Array.isArray(val) && val.length > 0
    return val !== undefined && val !== ""
  })
  const hasAnyActiveFilters = activeTab === "companies" ? hasActiveCompanyFilters : hasActiveProductFilters

  const handleAddCompanyFilter = (key: string, value: string) => {
    setCompanyFilters((prev: any) => {
      const current = prev[key] || []
      if (!current.includes(value)) {
        return { ...prev, [key]: [...current, value] }
      }
      return prev
    })
  }

  const handleRemoveCompanyFilter = (key: string, value: string) => {
    setCompanyFilters((prev: any) => {
      const current = prev[key] || []
      const updated = current.filter((v: string) => v !== value)
      const next = { ...prev }
      if (updated.length > 0) {
        next[key] = updated
      } else {
        delete next[key]
      }
      return next
    })
  }

  const handleAddProductFilter = (key: string, value: any) => {
    setProductFilters((prev: any) => {
      if (key === "category") {
        const current = prev[key] || []
        if (!current.includes(value)) {
          return { ...prev, [key]: [...current, value] }
        }
        return prev
      }
      return { ...prev, [key]: value }
    })
  }

  const handleRemoveProductFilter = (key: string, value?: string) => {
    setProductFilters((prev: any) => {
      if (key === "category" && value) {
        const current = prev[key] || []
        const updated = current.filter((v: string) => v !== value)
        const next = { ...prev }
        if (updated.length > 0) {
          next[key] = updated
        } else {
          delete next[key]
        }
        return next
      }
      const newFilters = { ...prev }
      delete newFilters[key]
      return newFilters
    })
  }
  const isMobile = useIsMobile()
  const [isLargeScreen, setIsLargeScreen] = useState(true)

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1024)
    }
    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Rotating typing placeholder effect
  const placeholders = useMemo(() => {
    if (isMobile) {
      return [
        "e.g. Organic spices",
        "e.g. Cotton yarn",
        "e.g. Custom boxes"
      ]
    }
    return [
      "e.g. 500kg organic turmeric powder, EU-certified, delivery to Hamburg...",
      "e.g. Cotton yarn supplier with ISO certification for textile manufacturing...",
      "e.g. Custom packaging boxes with eco-friendly kraft paper, MOQ 1000..."
    ]
  }, [isMobile])

  const [placeholderText, setPlaceholderText] = useState("")

  useEffect(() => {
    let currentIdx = 0
    let charIdx = 0
    let isDeleting = false
    let timer: any

    const tick = () => {
      const fullText = placeholders[currentIdx]
      if (!isDeleting) {
        setPlaceholderText(fullText.substring(0, charIdx + 1))
        charIdx++

        if (charIdx === fullText.length) {
          timer = setTimeout(() => {
            isDeleting = true
            tick()
          }, 2500)
          return
        }
      } else {
        setPlaceholderText(fullText.substring(0, charIdx - 1))
        charIdx--

        if (charIdx === 0) {
          isDeleting = false
          currentIdx = (currentIdx + 1) % placeholders.length
          timer = setTimeout(tick, 500)
          return
        }
      }

      const speed = isDeleting ? 15 : 45
      timer = setTimeout(tick, speed)
    }

    tick()

    return () => clearTimeout(timer)
  }, [placeholders])

  // Extra RFQ fields
  const [rfqAddress, setRfqAddress] = useState("")
  const [rfqMoq, setRfqMoq] = useState("")
  const [rfqBudget, setRfqBudget] = useState("")
  const [rfqPaymentTerms, setRfqPaymentTerms] = useState("")
  const [rfqEstimatedTime, setRfqEstimatedTime] = useState("")
  const [rfqValidUntil, setRfqValidUntil] = useState("")

  const [rfqPreferredShipmentMode, setRfqPreferredShipmentMode] = useState("AIR")
  const [rfqShipmentType, setRfqShipmentType] = useState("DOORSTEP_DELIVERY")
  const [rfqOriginLocation, setRfqOriginLocation] = useState<any>(null)
  const [rfqShowLocationDropdown, setRfqShowLocationDropdown] = useState(false)

  const searchConfig = { label: "Search your City", url: "https://finance.devapi.zipaworld.com/api/auth/masters/city/manager" }
  
  const {
    searchTerm: rfqLocationSearchTerm,
    setSearchTerm: setRfqLocationSearchTerm,
    results: rfqLocationResults,
    setResults: setRfqLocationResults,
    isSearching: rfqIsSearchingLocation
  } = useSearchLocation(
    searchConfig.url, 
    500, 
    rfqOriginLocation?.city || rfqOriginLocation?.name || rfqOriginLocation?.pincode || rfqOriginLocation?.portName,
    (searchConfig as any)?.additionalPayload
  )

  useEffect(() => {
    if (rfqOriginLocation && !rfqLocationSearchTerm) {
      setRfqLocationSearchTerm(rfqOriginLocation.city || rfqOriginLocation.name || rfqOriginLocation.pincode || rfqOriginLocation.portName || "")
    }
  }, [rfqOriginLocation])

  useEffect(() => {
    if (activeTab === "companies" && activeCompanyRfqTarget) {
      setTimeout(() => {
        const el = document.getElementById("company-rfq-form-container")
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" })
        }
      }, 60)
    }
  }, [activeCompanyRfqTarget, activeTab])

  useEffect(() => {
    if (activeTab === "products" && activeProductRfqTarget) {
      setTimeout(() => {
        const el = document.getElementById("product-rfq-form-container")
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" })
        }
      }, 60)
    }
  }, [activeProductRfqTarget, activeTab])

  const { data: categoriesData, isLoading: categoriesLoading } = useProductCategories()
  const { data: filtersData } = useSearchFilters()

  const companyFilterOptions = useMemo(() => {
    if (!filtersData?.companyFilters) {
      return COMPANY_FILTER_OPTIONS
    }
    // Filter out empty options or options that aren't arrays
    const dynamicFilters: Record<string, string[]> = {}
    Object.entries(filtersData.companyFilters).forEach(([key, val]) => {
      if (Array.isArray(val) && val.length > 0) {
        dynamicFilters[key] = val
      }
    })
    return Object.keys(dynamicFilters).length > 0 ? dynamicFilters : COMPANY_FILTER_OPTIONS
  }, [filtersData])

  const productFilterOptions = useMemo(() => {
    const categoryList = categoriesData || filtersData?.productFilters?.category || []
    const moqList = filtersData?.productFilters?.minMoq && typeof filtersData.productFilters.minMoq !== "string"
      ? [1, 10, 50, 100, 250, 500, 1000] // fallback Moq ranges
      : PRODUCT_FILTER_OPTIONS_BASE.minMoq
    
    return {
      category: categoryList,
      minMoq: moqList
    }
  }, [categoriesData, filtersData])

  useEffect(() => {
    setHasSearchResults(!!data && !isError && !!urlSearchTerm)
  }, [data, isError, urlSearchTerm, setHasSearchResults])
  const createRfqMutation = useCreateRfq()
  const token = useAuthStore((s) => s.accessToken)
  const { data: recentSearches, refetch: refetchRecentSearches } = useRecentSearches(token)
  const [isMainFocused, setIsMainFocused] = useState(false)
  const mainSearchRef = useRef<HTMLFormElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (mainSearchRef.current && !mainSearchRef.current.contains(e.target as Node)) {
        setIsMainFocused(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const mergedSuppliers = useMemo<MergedSupplierItem[]>(() => {
    if (!data) return []
    const internals: MergedSupplierItem[] = (data.companies || []).map((c) => ({
      id: c.id,
      type: "internal",
      name: c.name,
      location: [c.city, c.state, c.country].filter(Boolean).join(", ") || "Location not specified",
      description: c.description,
      companyType: c.companyType,
      website: c.website,
      email: c.email,
      phoneNumber: c.phoneNumber,
      rank: c.rank,
      verificationStatus: c.verificationStatus,
      tags: c.tags,
      yearEstablished: c.yearEstablished || undefined,
      annualRevenue: c.annualRevenue || undefined,
      original: c,
    }))

    const leads: MergedSupplierItem[] = (data.leadSellers || []).map((l, idx) => ({
      id: l.id || `lead-${l.name || idx}`,
      type: "lead",
      name: l.name,
      location: [l.city, l.state].filter(Boolean).join(", ") || l.addressLine1 || "Location not specified",
      description: l.description || undefined,
      website: l.website || undefined,
      email: undefined,
      phoneNumber: undefined,
      rank: undefined,
      verificationStatus: undefined,
      tags: undefined,
      companyType: l.companyType || undefined,
      yearEstablished: l.yearEstablished || undefined,
      annualRevenue: l.annualRevenue || undefined,
      original: l,
    }))

    return [...internals, ...leads]
  }, [data])

  const executeSearch = (rawQuery?: string) => {
    setIsMainFocused(false)
    ;(document.activeElement as HTMLElement)?.blur()
    const query = (rawQuery ?? inputValue).trim()
    if (query) {
      commitSearch(query)
    } else {
      setSearchQuery("")
      setIsMultiSelectActive(false)
      setSelectedCompanyIds([])
      setActiveCompanyRfqTarget(null)
      setActiveProductRfqTarget(null)
      setSuccessMessage(null)
      navigate({
        search: (prev: any) => ({
          ...prev,
          searchTerm: undefined,
          productId: undefined,
          companyId: undefined,
          companyType: undefined,
          highlightVariantId: undefined,
        }),
      } as any)
    }
  }

  const handleSearchSubmit = (e: FormEvent) => {
    e.preventDefault()
    executeSearch()
  }

  const handleClear = () => {
    setInputValue("")
    if (!hasSearchResults) {
      setSearchQuery("")
      setIsMultiSelectActive(false)
      setSelectedCompanyIds([])
      setActiveCompanyRfqTarget(null)
      setActiveProductRfqTarget(null)
      setSuccessMessage(null)
      navigate({
        search: (prev: any) => ({
          ...prev,
          searchTerm: undefined,
          productId: undefined,
          companyId: undefined,
          companyType: undefined,
          highlightVariantId: undefined,
        }),
      } as any)
    }
  }

  const handleRfqSubmit = (type: "companies" | "products") => {
    const target = type === "companies" ? activeCompanyRfqTarget : activeProductRfqTarget
    const message = type === "companies" ? companyQueryMessage : productQueryMessage
    const qty = type === "companies" ? companyQuantity : productQuantity

    if (!target) return
    const buyerId = (user?.buyer as { id?: string })?.id ?? user?.id ?? "string"

    const resolvedValidUntil = rfqValidUntil
      ? new Date(rfqValidUntil).toISOString()
      : undefined

    const payload: any = {
      buyerId,
      type: "CLOSE",
      queryMessage: message || undefined,
      // validUntil: resolvedValidUntil,
      quantity: qty === "" ? null : Number(qty),
      measureUnit: "pcs", // Default fallback unit for quick RFQs
      moq: rfqMoq || undefined,
      budget: rfqBudget ? Number(rfqBudget) : undefined,
      currencyCode: "INR", // Default fallback currency for quick RFQs
      paymentTerms: rfqPaymentTerms || undefined,
      leadTime: rfqEstimatedTime ? `${rfqEstimatedTime} Days` : undefined,
      destinationCityId: rfqOriginLocation?._id || undefined,
      preferredShipmentMode: rfqPreferredShipmentMode || undefined,
      shipmentType: rfqShipmentType || undefined,
      targets: { quantity: qty === "" ? null : Number(qty) },
    }

    if (target.type === "single-product") {
      if (target.product.productVariantId) {
        payload.targets.productVariantId = target.product.productVariantId
      } else {
        payload.targets.productId = target.product.id
      }
    } else if (target.type === "single-company") {
      payload.targets.companyIds = [target.company.id]
    } else if (target.type === "single-external") {
      if (target.seller.id) {
        payload.targets.externalSellerIds = [target.seller.id]
      }
    } else if (target.type === "single-lead") {
      payload.targets.externalSellerIds = [target.seller.id]
    } else if (target.type === "multi-company") {
      if (target.companies && target.companies.length > 0) {
        payload.targets.companyIds = target.companies.map((c) => c.id)
      }
      if (target.leadSellers && target.leadSellers.length > 0) {
        payload.targets.externalSellerIds = target.leadSellers.map((s) => s.id)
      }
    }

    console.log("Submitting direct RFQ payload (FindSupplierPage):", payload)
    createRfqMutation.mutate(payload, {
      onSuccess: (data) => {
        const id = (data as any)?.buyerToAdminRfq?.id || data?.id || null
        if (type === "companies") {
          setActiveCompanyRfqTarget(null)
          setCompanyQueryMessage("")
          setCompanyQuantity("")
          setIsMultiSelectActive(false)
          setSelectedCompanyIds([])
        } else {
          setActiveProductRfqTarget(null)
          setProductQueryMessage("")
          setProductQuantity("")
        }
        setRfqAddress("")
        setRfqMoq("")
        setRfqBudget("")
        setRfqPaymentTerms("")
        setRfqEstimatedTime("")
        setRfqValidUntil("")
        setRfqPreferredShipmentMode("AIR")
        setRfqShipmentType("DOORSTEP_DELIVERY")
        setRfqOriginLocation(null)
        setRfqLocationSearchTerm("")
        setRfqLocationResults([])
        
        // Redirect to RFQ list page
        navigate({ to: "/rfqs" })
      },
      onError: (err) => console.error(err),
    })
  }

  const renderTransportationFields = () => {
    return (
      <div className="space-y-4 sm:col-span-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-bold uppercase tracking-wider text-secondary">
              Preferred Shipment Mode
            </label>
            <select
              value={rfqPreferredShipmentMode}
              onChange={(e) => setRfqPreferredShipmentMode(e.target.value)}
              className="w-full text-xs font-semibold text-on-surface bg-white border border-outline-variant/30 rounded-xl px-3 py-2.5 outline-none focus:border-[#be001e] focus:ring-2 focus:ring-[#be001e]/10 transition-all cursor-pointer"
            >
              <option value="AIR">AIR</option>
              <option value="OCEAN">OCEAN</option>
              <option value="ROAD">ROAD</option>
              <option value="RAIL">RAIL</option>
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-bold uppercase tracking-wider text-secondary">
              Shipment Type
            </label>
            <select
              value={rfqShipmentType}
              onChange={(e) => setRfqShipmentType(e.target.value)}
              className="w-full text-xs font-semibold text-on-surface bg-white border border-outline-variant/30 rounded-xl px-3 py-2.5 outline-none focus:border-[#be001e] focus:ring-2 focus:ring-[#be001e]/10 transition-all cursor-pointer"
            >
              <option value="DOORSTEP_DELIVERY">DOORSTEP DELIVERY</option>
              <option value="PORT_TO_PORT">PORT TO PORT</option>
              <option value="WAREHOUSE_PICKUP">WAREHOUSE PICKUP</option>
            </select>
          </div>
        </div>

        <div className="flex flex-col gap-1.5 relative">
          <label className="text-[10px] font-bold uppercase tracking-wider text-secondary flex items-center justify-between">
            <span>Destination City / Port</span>
            {rfqOriginLocation && (
              <button
                type="button"
                onClick={() => {
                  setRfqOriginLocation(null)
                  setRfqLocationSearchTerm("")
                  setRfqLocationResults([])
                }}
                className="text-[#be001e] hover:underline cursor-pointer lowercase text-[9.5px]"
              >
                Clear location
              </button>
            )}
          </label>
          <div className="relative">
            <input
              type="text"
              value={rfqLocationSearchTerm}
              onChange={(e) => {
                setRfqLocationSearchTerm(e.target.value)
                setRfqShowLocationDropdown(true)
              }}
              onFocus={() => setRfqShowLocationDropdown(true)}
              placeholder="Search destination city or port..."
              className="w-full text-xs font-semibold text-on-surface bg-white border border-outline-variant/30 rounded-xl pl-9 pr-3 py-2.5 outline-none focus:border-[#be001e] focus:ring-2 focus:ring-[#be001e]/10 transition-all"
            />
            <MapPin className="size-4 text-secondary/60 absolute left-3 top-1/2 -translate-y-1/2" />
            {rfqIsSearchingLocation && (
              <Loader2 className="size-3.5 animate-spin text-[#be001e] absolute right-3 top-1/2 -translate-y-1/2" />
            )}
          </div>
          {rfqShowLocationDropdown && rfqLocationResults && rfqLocationResults.length > 0 && (
            <ul className="absolute top-full left-0 right-0 mt-1 bg-white border border-outline-variant/30 rounded-xl shadow-lg max-h-48 overflow-y-auto z-50 py-1">
              {rfqLocationResults.map((loc: any, i: number) => (
                <li
                  key={loc._id || i}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setRfqOriginLocation(loc)
                    setRfqLocationSearchTerm(loc.city || loc.name || loc.pincode || loc.portName || "")
                    setRfqShowLocationDropdown(false)
                  }}
                  className="px-3 py-2 text-[12px] font-medium text-on-surface hover:bg-[#be001e]/5 hover:text-[#be001e] cursor-pointer transition-colors border-b border-outline-variant/10 last:border-0"
                >
                  {loc.city || loc.name || loc.pincode || loc.portName}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    )
  }

  const getCompanyTargetPayload = () => {
    if (!activeCompanyRfqTarget) return null;
    const target = activeCompanyRfqTarget;
    const payload: any = {};
    if (target.type === "single-company") {
      payload.companyIds = [target.company.id]
    } else if (target.type === "single-lead") {
      payload.externalSellerIds = [target.seller.id]
    } else if (target.type === "multi-company") {
      if (target.companies && target.companies.length > 0) {
        payload.companyIds = target.companies.map((c) => c.id)
      }
      if (target.externalSellers && target.externalSellers.length > 0) {
        payload.externalSearchId = data?.externalSearchId || undefined
      }
      if (target.leadSellers && target.leadSellers.length > 0) {
        payload.externalSellerIds = target.leadSellers.map((s) => s.id)
      }
    }
    return payload;
  }

  const getProductTargetPayload = () => {
    if (!activeProductRfqTarget) return null;
    const target = activeProductRfqTarget;
    const payload: any = {};
    if (target.type === "single-product") {
      payload.productId = target.product.productId || target.product.id
    } else if (target.type === "single-external") {
      if (target.seller.id) {
        payload.externalSellerIds = [target.seller.id]
      }
    }
    return payload;
  }

  const handleSelectToggle = (id: string) => {
    setSelectedCompanyIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    )
  }

  const isAllSelected = mergedSuppliers.length > 0 && selectedCompanyIds.length === mergedSuppliers.length

  const handleSelectAllToggle = () => {
    if (isAllSelected) {
      setSelectedCompanyIds([])
    } else {
      setSelectedCompanyIds(mergedSuppliers.map((s) => s.id))
    }
  }

  const handleCancelMultiSelect = () => {
    setIsMultiSelectActive(false)
    setSelectedCompanyIds([])
    setActiveCompanyRfqTarget(null)
  }

  const handleRequestQuoteMulti = () => {
    const selectedItems = mergedSuppliers.filter((s) => selectedCompanyIds.includes(s.id))
    const companies = selectedItems
      .filter((s) => s.type === "internal")
      .map((s) => s.original as CompanySearchResult)
    const leadSellers = selectedItems
      .filter((s) => s.type === "lead")
      .map((s) => s.original as LeadSeller)
    
    setActiveCompanyRfqTarget({
      type: "multi-company",
      companies,
      leadSellers,
    })
  }

  const companyMap = new Map<string, string>()
  if (data?.companies) data.companies.forEach((c) => companyMap.set(c.id, c.name))

  const hasResults = !isError && urlSearchTerm && !!data && !isFetching


  return (
    <div className={cn(
      "space-y-0 -mx-3 sm:-mx-6 -mt-6",
      hasResults 
        ? "bg-[#f5f5f5]" 
        : "pb-12 bg-gradient-to-br from-[#fbf2f0] to-[#fefdfd] min-h-[calc(100vh-6rem)]"
    )}>
          {/* ─── Hero / Omni-Search ──────────────────────────────────────────────── */}
          {!urlSearchTerm && (
            <section className="relative bg-gradient-to-br from-[#fbf2f0] to-[#fefdfd] border-b border-slate-200/50 px-4 sm:px-8 py-14 sm:py-10 text-center animate-in fade-in duration-200">
              {/* Dot grid overlay */}
              <div className="absolute inset-0 bg-[radial-gradient(#be001e_1.2px,transparent_1.2px)] [background-size:28px_28px] opacity-[0.05] pointer-events-none" />

              <div className="relative max-w-4xl mx-auto text-center space-y-3.5">
                <h1 className="text-3xl sm:text-3xl md:text-4xl font-black text-slate-800 tracking-tight leading-tight">
                  Find your next <span className="text-[#be001e]">partner</span>
                </h1>
                <p className="text-sm sm:text-base md:text-[17px] text-slate-600 max-w-4xl mx-auto font-bold tracking-tight leading-relaxed">
                  Describe your requirement in plain language. Our AI will scan 50,000+ verified global suppliers to find the perfect match.
                </p>

                {/* Hero Search */}
                <form onSubmit={handleSearchSubmit} className="max-w-5xl mx-auto mt-6 relative" ref={mainSearchRef}>
                  <div className="hover-glow-trigger theme-hover-shadow relative transition-all duration-300 p-0.5 h-[118px]">
                    {/* Animated/Rotating Gradient shadow glow */}
                    <div className="rotating-gradient-border-glow-shadow" />
                    <div className="rotating-gradient-border shadow-lg shadow-[#be001e]/5 transition-all duration-300 h-full">
                      <div className="rotating-gradient-border-content p-1.5 h-full justify-between">
                        {/* Row 1: Text Input */}
                        <div className="relative flex items-center w-full">
                          <input
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onFocus={() => {
                              setIsMainFocused(true)
                              refetchRecentSearches()
                            }}
                            onBlur={() => setTimeout(() => setIsMainFocused(false), 200)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault()
                                executeSearch()
                              } else if (e.key === "Escape") {
                                setIsMainFocused(false)
                                ;(e.target as HTMLInputElement).blur()
                              }
                            }}
                            placeholder={placeholderText}
                            className="flex-1 min-w-0 py-3 pl-4 pr-10 text-[14px] text-slate-800 placeholder:text-slate-600 outline-none bg-transparent font-medium border-0 focus:ring-0 truncate"
                          />
                          {inputValue && (
                            <button type="button" onClick={handleClear} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 cursor-pointer shrink-0">
                              <X className="size-4" />
                            </button>
                          )}
                        </div>
                        {/* Row 2: Action Bar */}
                        <div className="flex items-center justify-end px-3 pb-2 pt-1">
                          {/* Right: Search button */}
                          <button
                            type="submit"
                            disabled={isLoading || isFetching}
                            className="bg-gradient-to-r from-[#be001e] to-[#e6202f] hover:opacity-95 text-white p-2.5 sm:px-7 sm:py-2.5 rounded-full font-bold text-[13px] transition-all cursor-pointer shrink-0 flex items-center gap-1.5 active:scale-95 shadow-md shadow-[#be001e]/15"
                          >
                            {(isLoading || isFetching) ? (
                              <Loader2 className="size-3.5 animate-spin" />
                            ) : (
                              <Search className="size-3.5" />
                            )}
                            <span className="hidden sm:inline">Search</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Recent searches - positioned relative to search wrapper, directly below the search bar! */}
                    {isMainFocused && recentSearches && recentSearches.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden text-left z-50">
                        <span className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider block px-4 py-2 border-b border-slate-100 bg-slate-50/50">
                          Recent Searches
                        </span>
                        <div className="max-h-[180px] overflow-y-auto">
                          {recentSearches.map((recent) => (
                            <button
                              key={recent.id}
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault()
                                executeSearch(recent.query)
                              }}
                              className="w-full px-4 py-2.5 text-left text-[13px] font-medium hover:bg-slate-50 border-b border-slate-100 last:border-0 flex items-center gap-3 transition-colors"
                            >
                              <Clock className="size-4 text-slate-400 shrink-0" />
                              <span className="truncate">{recent.query}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Trending searches row */}
                  <div className="flex flex-wrap items-center justify-center gap-2 mt-4 text-[12.5px] font-semibold text-slate-500 animate-in fade-in slide-in-from-top-1 duration-300">
                    <span className="flex items-center gap-1.5 text-slate-700 font-extrabold shrink-0 mr-1">
                      <TrendingUp className="size-4 text-[#be001e]" />
                      Trending:
                    </span>
                    {TRENDING.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => commitSearch(tag)}
                        className="bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:border-[#be001e]/30 px-3.5 py-1.5 rounded-full text-slate-600 font-bold text-xs transition-all cursor-pointer active:scale-95 border border-slate-200 shadow-2xs hover:shadow-xs"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </form>
              </div>
            </section>
          )}

          {/* ─── Content Area ────────────────────────────────────────────────────── */}
          <div className={cn("space-y-6", isMobile ? "px-2 pb-4 mt-3" : "px-6 pb-8 mt-6")}>

            {/* Success banner */}
            {successMessage && (
              <div className="flex items-center justify-between rounded-xl border border-emerald-100 bg-emerald-50 px-5 py-4 text-[13px] text-emerald-800">
                <span className="font-medium">{successMessage}</span>
                <button onClick={() => setSuccessMessage(null)} className="text-emerald-500 hover:text-emerald-700">
                  <X className="size-4" />
                </button>
              </div>
            )}

            {/* Loading state (initial or refetch) */}
            {(isLoading || isFetching) && (
              <div className="pt-4">
                <AIProgressBar />
              </div>
            )}

            {/* Error */}
            {isError && !isFetching && (
              <div className="flex items-center gap-3 rounded-xl border border-red-100 bg-red-50 p-5 text-[13px] text-[#be001e]">
                <AlertCircle className="size-5 shrink-0" />
                <p className="font-medium">{(error as Error).message || "An error occurred during search."}</p>
              </div>
            )}

            {/* Empty / idle state — how it works */}
            {!urlSearchTerm && !isLoading && !isFetching && (
              <div className="space-y-12 animate-in fade-in duration-300">
                {selectedDetailProductId ? (
                  <ProductDetailsView
                    productId={selectedDetailProductId}
                    highlightVariantId={highlightVariantId}
                    onBack={() => {
                      navigate({
                        search: (prev) => {
                          const { productId, highlightVariantId, ...rest } = prev as any
                          return rest
                        }
                      } as any)
                    }}
                    companyName=""
                    onQuickInquiry={(prod) => {
                      setActiveProductRfqTarget({
                        type: "single-product",
                        product: prod,
                        companyName: prod.company?.name || "",
                      })
                    }}
                  />
                ) : (
                  <div className="space-y-10">
                    {/* ── Section 2: RFQ Promotional Card ─────────────────────────────── */}
                    <section>
                      <div
                        className="group relative bg-white border border-slate-200 rounded-2xl p-4 flex flex-col sm:flex-row items-start gap-6 hover:shadow-lg hover:shadow-[#be001e]/5 transition-all duration-300 cursor-pointer overflow-hidden"
                        onClick={() => navigate({ to: "/submit-rfq" })}
                      >
                        {/* Subtle red gradient backdrop */}
                        <div className="absolute inset-0 bg-gradient-to-br from-[#be001e]/3 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-2xl" />

                        {/* Icon */}
                        <div className="hidden sm:flex shrink-0 size-12 rounded-2xl bg-[#be001e]/10 items-center justify-center group-hover:bg-[#be001e]/15 transition-colors">
                          <FileText className="size-6 text-[#be001e]" />
                        </div>

                        {/* Text */}
                        <div className="flex-1 min-w-0 space-y-1">
                          <h2 className="text-[16px] sm:text-[18px] font-bold text-slate-800 tracking-tight text-left">
                            Get Quotes for Your Custom Requirement
                          </h2>
                          <p className="text-[13px] text-slate-500 leading-relaxed text-left">
                            Tell us what you need and receive competitive quotations from verified suppliers worldwide.
                          </p>
                          {/* Benefit chips */}
                          <div className="flex flex-wrap gap-2 pt-2">
                            {[
                              { icon: CheckCircle2, label: "Multiple Quotes" },
                              { icon: Users, label: "Verified Suppliers" },
                              { icon: BadgePercent, label: "Better Pricing" },
                            ].map(({ icon: Icon, label }) => (
                              <span
                                key={label}
                                className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-600 text-[11px] font-semibold px-2.5 py-1 rounded-full"
                              >
                                <Icon className="size-3 text-[#be001e]" />
                                {label}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* CTA */}
                        <div className="shrink-0 self-end sm:self-start">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate({ to: "/submit-rfq" })
                            }}
                            className="flex items-center gap-2 bg-[#be001e] sm:bg-white hover:bg-[#e6202f] sm:hover:bg-[#be001e]/5 text-white sm:text-slate-600 sm:hover:text-[#be001e] px-4 sm:px-3.5 py-1.5 rounded-full font-bold text-xs transition-all cursor-pointer active:scale-95 border border-transparent sm:border-slate-200 sm:hover:border-[#be001e]/30 shadow-sm sm:shadow-2xs hover:shadow-md sm:hover:shadow-xs"
                          >
                            Get Quotes
                          </button>
                        </div>
                      </div>
                    </section>

                    {/* ── Section 3: Categories Carousel ──────────────────────────────── */}
                    <section className="space-y-3 text-left">
                      <div className="flex items-center gap-2 mb-5">
                        <span className="w-1 h-4 bg-[#be001e] rounded-full shrink-0" />
                        <h3 className="text-[15px] sm:text-[17px] font-extrabold text-slate-800 tracking-tight">Categories you may like</h3>
                      </div>

                      {loadingCategories ? (
                        <div className="flex gap-3 overflow-x-auto scrollbar-none px-1 py-1">
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((n) => (
                            <div key={n} className="flex flex-col items-center gap-2 text-center shrink-0 w-[76px] p-2 rounded-xl border border-slate-100 bg-white animate-pulse">
                              <div className="w-10 h-10 rounded-xl bg-slate-200" />
                              <div className="h-2.5 w-12 bg-slate-200 rounded-sm" />
                            </div>
                          ))}
                        </div>
                      ) : isCategoriesError ? (
                        <div className="flex items-center justify-center p-6 bg-white border border-red-100 rounded-2xl gap-3">
                          <AlertCircle className="size-4 text-[#be001e]" />
                          <span className="text-xs font-bold text-slate-800">Failed to load categories</span>
                        </div>
                      ) : categories.length === 0 ? (
                        <div className="bg-white border border-slate-200/60 rounded-2xl p-8 text-center text-slate-400 font-medium text-sm">
                          No categories available at the moment.
                        </div>
                      ) : (
                        <div className="relative group/carousel">
                          {showLeftArrow && (
                            <button type="button" onClick={() => carouselRef.current?.scrollBy({ left: -260, behavior: "smooth" })}
                              className="absolute left-[-10px] top-[35%] -translate-y-1/2 z-10 flex size-8 items-center justify-center rounded-full border border-slate-200 bg-white shadow-md text-slate-600 hover:text-[#be001e] hover:border-[#be001e]/30 transition-all hover:scale-105 cursor-pointer">
                              <ChevronRight className="size-4 rotate-180" />
                            </button>
                          )}
                          <div ref={carouselRef} onScroll={checkScroll} className="flex gap-3 sm:gap-5 overflow-x-auto scroll-smooth scrollbar-none px-1">
                            {categories.map((cat) => {
                              const icon = getCategoryIcon(cat)
                              return (
                                <button
                                  key={cat}
                                  onClick={() => handleCategoryClick(cat)}
                                  className="flex flex-col items-center gap-2 transition-all duration-200 text-center group cursor-pointer shrink-0 w-[76px] sm:w-[88px] pb-2 relative"
                                >
                                  <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-slate-50 flex items-center justify-center transition-all group-hover:bg-[#be001e]/8 group-hover:scale-105 text-slate-600 group-hover:text-[#be001e] shadow-sm">
                                    {icon}
                                  </div>
                                  <span className="text-[10px] sm:text-[11px] font-semibold text-slate-600 group-hover:text-[#be001e] tracking-tight leading-tight line-clamp-2 max-w-[76px] sm:max-w-[84px] transition-colors capitalize">
                                    {cat}
                                  </span>
                                  <div className="absolute bottom-0 left-2 right-2 h-[2px] bg-transparent group-hover:bg-[#be001e] transition-colors rounded-full" />
                                </button>
                              )
                            })}
                          </div>
                          {showRightArrow && (
                            <button type="button" onClick={() => carouselRef.current?.scrollBy({ left: 260, behavior: "smooth" })}
                              className="absolute right-[-8px] top-[35%] -translate-y-1/2 z-10 flex size-8 items-center justify-center rounded-full border border-slate-200 bg-white shadow-md text-slate-600 hover:text-[#be001e] hover:border-[#be001e]/30 transition-all hover:scale-105 cursor-pointer">
                              <ChevronRight className="size-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </section>

                    {/* ── Section 4: Suppliers Carousel ───────────────────────────────── */}
                    <section className="space-y-3 text-left">
                      <div className="flex items-center gap-2">
                        <span className="w-1 h-4 bg-[#be001e] rounded-full shrink-0" />
                        <h3 className="text-[15px] sm:text-[17px] font-extrabold text-slate-800 tracking-tight">Suppliers you may like</h3>
                      </div>
                      {loadingSuppliers ? (
                        <div className="flex gap-4 overflow-x-auto scrollbar-none pt-2 pb-2 -mx-1 px-1">
                          {[1, 2, 3].map((n) => <div key={n} className="shrink-0 w-[320px]"><SupplierCard isLoading={true} /></div>)}
                        </div>
                      ) : isSuppliersError ? (
                        <div className="flex items-center justify-center p-6 bg-white border border-red-100 rounded-2xl gap-3">
                          <AlertCircle className="size-4 text-[#be001e]" />
                          <span className="text-xs font-bold text-slate-800">Failed to load suppliers</span>
                        </div>
                      ) : recommendedSuppliers.length === 0 ? (
                        <div className="bg-white border border-slate-200/60 rounded-2xl p-8 text-center text-slate-400 font-medium text-sm">
                          No recommended suppliers right now.
                        </div>
                      ) : (
                        <div className="relative group/carousel">
                          {showSupplierLeftArrow && (
                            <button type="button" onClick={() => supplierCarouselRef.current?.scrollBy({ left: -320, behavior: "smooth" })}
                              className="absolute left-[-8px] top-[40%] -translate-y-1/2 z-10 flex size-8 items-center justify-center rounded-full border border-slate-200 bg-white shadow-md text-slate-600 hover:text-[#be001e] hover:border-[#be001e]/30 transition-all hover:scale-105 cursor-pointer">
                              <ChevronRight className="size-4 rotate-180" />
                            </button>
                          )}
                          <div ref={supplierCarouselRef} onScroll={checkSupplierScroll} className="flex gap-4 overflow-x-auto scroll-smooth scrollbar-none pt-2 pb-2">
                            {recommendedSuppliers.map((supplier) => (
                              <div key={supplier.id} className="shrink-0 w-[320px] transition-all duration-300 hover:-translate-y-1 rounded-xl">
                                <SupplierCard
                                  supplier={{ ...supplier, type: "internal" }}
                                  onViewDetails={() => handleSupplierClick(supplier)}
                                />
                              </div>
                            ))}
                          </div>
                          {showSupplierRightArrow && (
                            <button type="button" onClick={() => supplierCarouselRef.current?.scrollBy({ left: 320, behavior: "smooth" })}
                              className="absolute right-[-8px] top-[40%] -translate-y-1/2 z-10 flex size-8 items-center justify-center rounded-full border border-slate-200 bg-white shadow-md text-slate-600 hover:text-[#be001e] hover:border-[#be001e]/30 transition-all hover:scale-105 cursor-pointer">
                              <ChevronRight className="size-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </section>

                    {/* ── Section 5: Why Source with Enga? ────────────────────────────── */}
                    <WhySourceWithEnga />
                  </div>
                )}
              </div>
            )}

            {/* ─── Results ──────────────────────────────────────────────────────── */}
            {hasResults && (
              <div className="space-y-5">



                {/* Sticky Headers Group (Tabs + Filters) */}
                <div 
                  style={{ top: `${topbarHeight}px` }}
                  className="sticky z-20 bg-[#f5f5f5] -mx-3 sm:-mx-6 border-b border-outline-variant/30 flex flex-col transition-all duration-200"
                >
                  {/* Results control header */}
                  <div 
                    ref={tabsRef}
                    className={cn(
                      "px-3 sm:px-6 py-3 border-b border-outline-variant/15 transition-all duration-200",
                      (isMultiSelectActive || activeTab === "products")
                        ? "flex flex-row items-center justify-between gap-3 sm:gap-4"
                        : "flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4"
                    )}
                  >
                    <div className="flex gap-4 sm:gap-5 items-center">
                      <button
                        onClick={() => {
                          setSuccessMessage(null)
                          const filters = Object.keys(companyFiltersStore || {}).length > 0
                            ? JSON.stringify(companyFiltersStore)
                            : undefined
                          navigate({
                            to: "/explore/suppliers",
                            search: (prev: any) => ({
                              ...prev,
                              companyFilters: filters,
                              productFilters: undefined,
                              productId: undefined,
                              companyId: undefined,
                              companyType: undefined,
                              highlightVariantId: undefined,
                            }),
                          })
                        }}
                        className={cn(
                          "relative pb-1.5 px-1 text-[13px] font-bold transition-colors cursor-pointer",
                          activeTab === "companies"
                            ? "text-on-surface border-b-2 border-[#be001e]"
                            : "text-secondary hover:text-on-surface border-b-2 border-transparent"
                        )}
                      >
                        Suppliers
                        <span className={cn(
                          "ml-1.5 px-1.5 py-0.5 rounded text-[10px]",
                          activeTab === "companies" ? "bg-[#be001e]/5 text-[#be001e]" : "bg-surface-container text-secondary"
                        )}>
                          {mergedSuppliers.length}
                        </span>
                      </button>
                      <button
                        onClick={() => {
                          setSuccessMessage(null)
                          const filters = Object.keys(productFiltersStore || {}).length > 0
                            ? JSON.stringify(productFiltersStore)
                            : undefined
                          navigate({
                            to: "/explore/products",
                            search: (prev: any) => ({
                              ...prev,
                              productFilters: filters,
                              companyFilters: undefined,
                              productId: undefined,
                              companyId: undefined,
                              companyType: undefined,
                              highlightVariantId: undefined,
                            }),
                          })
                        }}
                        className={cn(
                          "relative pb-1.5 px-1 text-[13px] font-bold transition-colors cursor-pointer",
                          activeTab === "products"
                            ? "text-on-surface border-b-2 border-[#be001e]"
                            : "text-secondary hover:text-on-surface border-b-2 border-transparent"
                        )}
                      >
                        Products
                        <span className={cn(
                          "ml-1.5 px-1.5 py-0.5 rounded text-[10px]",
                          activeTab === "products" ? "bg-[#be001e]/5 text-[#be001e]" : "bg-surface-container text-secondary"
                        )}>
                          {(data?.products?.length || 0) + (data?.externalProducts?.length || 0)}
                        </span>
                      </button>
                    </div>

                    {/* Right side Contextual action buttons */}
                    <div className={cn(
                      "flex items-center gap-2 shrink-0",
                      (isMultiSelectActive || activeTab === "products")
                        ? "w-auto justify-end ml-auto"
                        : "w-full sm:w-auto justify-start sm:justify-end"
                    )}>
                      {activeTab === "companies" && mergedSuppliers.length > 0 && !selectedDetailCompany && !isMultiSelectActive && (
                        <button
                          type="button"
                          onClick={() => {
                            setIsMultiSelectActive(true)
                            setSelectedCompanyIds([])
                            setActiveCompanyRfqTarget(null)
                          }}
                          className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold transition-all border cursor-pointer shadow-sm bg-white border-outline-variant/30 text-secondary hover:text-on-surface hover:border-slate-400"
                        >
                          Select multiple for RFQ
                        </button>
                      )}
                      
                      <button
                        ref={filterButtonRef}
                        type="button"
                        onClick={() => setIsFilterPanelOpen(prev => !prev)}
                        className={cn(
                          "flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border transition-all duration-150 cursor-pointer shadow-sm",
                          isFilterPanelOpen
                            ? "bg-[#be001e] border-[#be001e] text-white"
                            : "bg-white border-outline-variant/30 text-secondary hover:text-[#be001e] hover:border-[#be001e]/40"
                        )}
                        aria-expanded={isFilterPanelOpen}
                        aria-controls="alibaba-filter-sidebar"
                      >
                        <SlidersHorizontal className="size-3" /> Filter
                      </button>
                    </div>
                  </div>

                  {/* Filters Row */}
                  {!selectedDetailCompany && !selectedDetailProductId && (
                    <div className="transition-all duration-200">

                      {/* Backdrop Overlay */}
                      {isFilterPanelOpen && createPortal(
                        <div 
                          className="fixed top-16 inset-x-0 bottom-0 z-[60] bg-black/10 transition-opacity animate-in fade-in duration-200"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsFilterPanelOpen(false);
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onTouchStart={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        />,
                        document.body
                      )}

                      {/* Sliding Filter Sidebar (Alibaba-style Drawer) */}
                      {isFilterPanelOpen && createPortal(
                        <div
                          ref={filterPanelRef}
                          id="alibaba-filter-sidebar"
                          className="fixed right-0 top-16 bottom-0 w-full max-w-[250px] bg-white z-[99] shadow-2xl flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-100"
                          role="dialog"
                          aria-modal="true"
                          aria-label="Filter Sidebar"
                          onMouseDown={(e) => e.stopPropagation()}
                          onTouchStart={(e) => e.stopPropagation()}
                        >
                          {/* Sidebar Header */}
                          <div className="flex items-center justify-between px-5 py-1 border-b border-slate-100 shrink-0">
                            <div>
                              <h3 className="text-[16px] font-bold text-slate-800 flex items-center gap-2">
                                <SlidersHorizontal className="size-4 text-[#be001e]" /> Filters
                              </h3>
                            </div>
                            <button
                              type="button"
                              onClick={() => setIsFilterPanelOpen(false)}
                              className="text-slate-400 hover:text-slate-600 hover:bg-slate-50 p-2 rounded-lg transition-colors cursor-pointer"
                            >
                              <X className="size-5" />
                            </button>
                          </div>

                          {/* Sidebar Scrollable Body */}
                          <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-4.5 scrollbar-thin">
                            {activeTab === "companies" ? (
                              Object.entries(companyFilterOptions).map(([key, options]) => {
                                const heading = key
                                  .replace(/([A-Z])/g, " $1")
                                  .trim()
                                  .replace(/^./, (str) => str.toUpperCase());
                                return (
                                  <div key={key} className="space-y-2">
                                    <h4 className="text-[11px] font-bold text-slate-800 uppercase tracking-wider px-2">
                                      {heading}
                                    </h4>
                                    <div className="flex flex-col gap-1">
                                      {Array.isArray(options) &&
                                        options.map((opt) => {
                                          const isActive = companyFilters[key]?.includes(opt);
                                          return (
                                            <button
                                              key={opt}
                                              type="button"
                                              onClick={() => {
                                                if (isActive) {
                                                  handleRemoveCompanyFilter(key, opt);
                                                } else {
                                                  handleAddCompanyFilter(key, opt);
                                                }
                                              }}
                                              className={cn(
                                                "text-[12px] px-3 py-1 rounded-xl text-left w-full transition-all focus:outline-none flex items-center gap-2.5 group cursor-pointer border",
                                                isActive
                                                  ? "bg-[#be001e]/5 border-[#be001e]/20 text-[#be001e] font-semibold"
                                                  : "bg-white border-transparent text-slate-800 hover:bg-slate-50 hover:text-slate-800"
                                              )}
                                            >
                                              {/* Custom Checkbox */}
                                              <div className={cn(
                                                "size-3.5 rounded flex items-center justify-center transition-all shrink-0 border",
                                                isActive 
                                                  ? "border-[#be001e] bg-[#be001e] text-white" 
                                                  : "border-slate-300 bg-white group-hover:border-[#be001e]/50"
                                              )}>
                                                {isActive && (
                                                  <svg className="size-2.5 stroke-[3]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                  </svg>
                                                )}
                                              </div>
                                              <span className="truncate">{opt}</span>
                                            </button>
                                          );
                                        })}
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <>
                                {/* Categories Section */}
                                <div className="space-y-2">
                                  <h4 className="text-[11px] font-bold text-slate-800 uppercase tracking-wider px-2">
                                    Product Categories
                                  </h4>
                                  <div className="flex flex-col gap-1">
                                    {Array.isArray(productFilterOptions.category) &&
                                      productFilterOptions.category.map((opt) => {
                                        const isActive = productFilters.category?.includes(opt);
                                        return (
                                          <button
                                            key={opt}
                                            type="button"
                                            onClick={() => {
                                              if (isActive) {
                                                 handleRemoveProductFilter("category", opt);
                                              } else {
                                                handleAddProductFilter("category", opt);
                                              }
                                            }}
                                            className={cn(
                                              "text-[12px] px-3 py-1 rounded-xl text-left w-full transition-all focus:outline-none flex items-center gap-2.5 group cursor-pointer border",
                                              isActive
                                                ? "bg-[#be001e]/5 border-[#be001e]/20 text-[#be001e] font-semibold"
                                                : "bg-white border-transparent text-slate-800 hover:bg-slate-50 hover:text-slate-800"
                                            )}
                                          >
                                            {/* Custom Checkbox */}
                                            <div className={cn(
                                              "size-3.5 rounded flex items-center justify-center transition-all shrink-0 border",
                                              isActive 
                                                ? "border-[#be001e] bg-[#be001e] text-white" 
                                                : "border-slate-300 bg-white group-hover:border-[#be001e]/50"
                                            )}>
                                              {isActive && (
                                                <svg className="size-2.5 stroke-[3]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                </svg>
                                              )}
                                            </div>
                                            <span className="truncate">{opt}</span>
                                          </button>
                                        );
                                      })}
                                  </div>
                                </div>

                                {/* MOQ Section */}
                                <div className="space-y-2">
                                  <h4 className="text-[11px] font-bold text-slate-800 uppercase tracking-wider px-2">
                                    Minimum MOQ
                                  </h4>
                                  <div className="flex flex-col gap-1">
                                    {Array.isArray(productFilterOptions.minMoq) &&
                                      productFilterOptions.minMoq.map((opt) => {
                                        const isActive = productFilters.minMoq === opt;
                                        return (
                                          <button
                                            key={opt}
                                            type="button"
                                            onClick={() => {
                                               if (isActive) { handleRemoveProductFilter("minMoq"); } else { handleAddProductFilter("minMoq", Number(opt)); }
                                            }}
                                            className={cn(
                                              "text-[12px] px-3 py-2.5 rounded-xl text-left w-full transition-all focus:outline-none flex items-center gap-2.5 group cursor-pointer border",
                                              isActive
                                                ? "bg-[#be001e]/5 border-[#be001e]/20 text-[#be001e] font-semibold"
                                                : "bg-white border-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-800"
                                            )}
                                          >
                                            {/* Custom Radio Button */}
                                            <div className={cn(
                                              "size-4 rounded-full flex items-center justify-center transition-all shrink-0 border",
                                              isActive 
                                                ? "border-[#be001e] bg-[#be001e]" 
                                                : "border-slate-300 bg-white group-hover:border-[#be001e]/50"
                                            )}>
                                              {isActive && (
                                                <div className="size-1.5 rounded-full bg-white animate-in zoom-in-50 duration-150" />
                                              )}
                                            </div>
                                            <span>{opt} units</span>
                                          </button>
                                        );
                                      })}
                                  </div>
                                </div>

                                {/* Price Section */}
                                <div className="space-y-2">
                                  <h4 className="text-[11px] font-bold text-slate-800 uppercase tracking-wider px-2">
                                    Price Range
                                  </h4>
                                  <div className="flex flex-col gap-3 px-1 pt-1">
                                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 shadow-sm focus-within:border-[#be001e]/50 focus-within:ring-2 focus-within:ring-[#be001e]/5 transition-all">
                                      <span className="text-[11px] font-medium text-slate-400 shrink-0">Min:</span>
                                      <input 
                                        type="number" 
                                        placeholder="0" 
                                        className="w-full text-[12px] font-semibold text-slate-700 border-none focus:ring-0 p-0 outline-none bg-transparent"
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") {
                                            const val = (e.target as HTMLInputElement).value;
                                            if (val) handleAddProductFilter("minPrice", Number(val));
                                            else handleRemoveProductFilter("minPrice");
                                          }
                                        }}
                                        defaultValue={productFilters.minPrice || ""}
                                      />
                                    </div>
                                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 shadow-sm focus-within:border-[#be001e]/50 focus-within:ring-2 focus-within:ring-[#be001e]/5 transition-all">
                                      <span className="text-[11px] font-medium text-slate-400 shrink-0">Max:</span>
                                      <input 
                                        type="number" 
                                        placeholder="No limit" 
                                        className="w-full text-[12px] font-semibold text-slate-700 border-none focus:ring-0 p-0 outline-none bg-transparent"
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") {
                                            const val = (e.target as HTMLInputElement).value;
                                            if (val) handleAddProductFilter("maxPrice", Number(val));
                                            else handleRemoveProductFilter("maxPrice");
                                          }
                                        }}
                                        defaultValue={productFilters.maxPrice || ""}
                                      />
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const minEl = document.querySelector('input[placeholder="0"]') as HTMLInputElement;
                                        const maxEl = document.querySelector('input[placeholder="No limit"]') as HTMLInputElement;
                                        if (minEl?.value) handleAddProductFilter("minPrice", Number(minEl.value));
                                        if (maxEl?.value) handleAddProductFilter("maxPrice", Number(maxEl.value));
                                      }}
                                      className="mt-1 bg-slate-800 hover:bg-slate-700 active:scale-98 text-white rounded-lg py-2 text-[12px] font-semibold transition"
                                    >
                                      Apply Price
                                    </button>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>

                          {/* Sidebar Footer */}
                          <div className="px-4 py-2.5 border-t border-slate-100 flex items-center justify-between bg-slate-50/50 shrink-0">
                            <button
                              type="button"
                              onClick={() => {
                                if (activeTab === "companies") setCompanyFilters({});
                                else setProductFilters({});
                                setIsFilterPanelOpen(false);
                              }}
                              className="text-[12px] font-semibold text-slate-500 hover:text-slate-800 hover:underline transition-colors px-1"
                            >
                              Reset All
                            </button>
                            <button
                              type="button"
                              onClick={() => setIsFilterPanelOpen(false)}
                              className="bg-[#be001e] hover:bg-[#be001e]/90 text-white font-bold text-[12px] px-5 py-1.5 rounded-lg shadow-sm transition active:scale-95 cursor-pointer"
                            >
                              Close
                            </button>
                          </div>
                        </div>,
                        document.body
                      )}

                      {/* Active Pills Area */}
                      {hasAnyActiveFilters && (
                        <div className="flex flex-wrap items-center gap-2 px-6 py-2.5">
                          {activeTab === "companies" && Object.entries(companyFilters).map(([key, values]) => {
                            if (!Array.isArray(values)) return null;
                            const displayKey = key.replace(/([A-Z])/g, ' $1').trim().replace(/^./, str => str.toUpperCase());
                            return values.map((val: string) => (
                              <div key={`${key}-${val}`} className="flex items-center gap-1.5 bg-[#be001e]/5 border border-[#be001e]/20 rounded-md pl-2.5 pr-1.5 py-1 text-[11px] text-[#be001e] font-medium transition-colors hover:bg-[#be001e]/10">
                                <span className="opacity-70">{displayKey}:</span> {val}
                                <button onClick={() => handleRemoveCompanyFilter(key, val)} className="p-0.5 hover:bg-[#be001e]/20 rounded-full ml-1 transition-colors">
                                  <X className="size-3" />
                                </button>
                              </div>
                            ))
                          })}

                          {activeTab === "products" && Object.entries(productFilters).map(([key, value]) => {
                            if (value === undefined) return null;
                            const displayKey = key.replace(/([A-Z])/g, ' $1').trim().replace(/^./, str => str.toUpperCase());
                            
                            if (key === "category" && Array.isArray(value)) {
                              return value.map((val: string) => (
                                <div key={`${key}-${val}`} className="flex items-center gap-1.5 bg-[#be001e]/5 border border-[#be001e]/20 rounded-md pl-2.5 pr-1.5 py-1 text-[11px] text-[#be001e] font-medium transition-colors hover:bg-[#be001e]/10">
                                  <span className="opacity-70">{displayKey}:</span> {val}
                                  <button onClick={() => handleRemoveProductFilter(key, val)} className="p-0.5 hover:bg-[#be001e]/20 rounded-full ml-1 transition-colors">
                                    <X className="size-3" />
                                  </button>
                                </div>
                              ))
                            }

                            return (
                              <div key={key} className="flex items-center gap-1.5 bg-[#be001e]/5 border border-[#be001e]/20 rounded-md pl-2.5 pr-1.5 py-1 text-[11px] text-[#be001e] font-medium transition-colors hover:bg-[#be001e]/10">
                                <span className="opacity-70">{displayKey}:</span> {value as any}
                                <button onClick={() => handleRemoveProductFilter(key)} className="p-0.5 hover:bg-[#be001e]/20 rounded-full ml-1 transition-colors">
                                  <X className="size-3" />
                                </button>
                              </div>
                            )
                          })}
                          
                          {((activeTab === "companies" && Object.keys(companyFilters).length > 0) || 
                            (activeTab === "products" && Object.keys(productFilters).length > 0)) && (
                            <button 
                              onClick={() => {
                                if (activeTab === "companies") setCompanyFilters({})
                                else setProductFilters({})
                              }}
                              className="text-[11px] font-semibold text-slate-500 hover:text-slate-800 hover:underline px-2"
                            >
                              Clear all
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Loading state (refetching) */}
                {isFetching && !isLoading && (
                  <div className="-mx-3 px-3 sm:mx-0 sm:px-0">
                    <AIProgressBar />
                  </div>
                )}



                {/* WIZARD FOR RFQ */}
                {activeCompanyRfqTarget || activeProductRfqTarget ? (
                  <div className="max-w-6xl mx-auto pb-4">
                    {activeCompanyRfqTarget && (
                      <SubmitRfqPage
                        target={activeCompanyRfqTarget}
                        isInline={false}
                        onSuccess={() => {
                          setActiveCompanyRfqTarget(null)
                          setIsMultiSelectActive(false)
                          setSelectedCompanyIds([])
                          navigate({ to: "/rfqs" })
                        }}
                        onCancel={() => {
                          setActiveCompanyRfqTarget(null)
                        }}
                      />
                    )}
                    {activeProductRfqTarget && (
                      <SubmitRfqPage
                        target={activeProductRfqTarget}
                        isInline={false}
                        onSuccess={() => {
                          setActiveProductRfqTarget(null)
                          navigate({ to: "/rfqs" })
                        }}
                        onCancel={() => {
                          setActiveProductRfqTarget(null)
                        }}
                      />
                    )}
                  </div>
                ) : (
                  <>
                    {/* Suppliers Tab */}
                    {activeTab === "companies" && (
                  <>
                    {selectedDetailCompany ? (
                      <CompanyDetailsView
                        companyId={selectedDetailCompany.id}
                        type={selectedDetailCompany.type as "internal" | "lead"}
                        leadSellers={data?.leadSellers}
                        onBack={() => handleClearDetails()}
                        onRequestQuote={(comp) => {
                          if (selectedDetailCompany.type === "internal") {
                            setActiveCompanyRfqTarget({ type: "single-company", company: comp })
                          } else {
                            setActiveCompanyRfqTarget({ type: "single-lead", seller: comp })
                          }
                          navigate({
                            search: (prev) => {
                              const { companyId, companyType, ...rest } = prev as any
                              return rest
                            }
                          } as any)
                        }}
                      />
                    ) : mergedSuppliers.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-outline-variant bg-surface-container-low/40 p-16 flex flex-col items-center justify-center text-center opacity-70">
                        <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-4 border border-outline-variant/20 shadow-sm">
                          <Search className="size-5 text-secondary" />
                        </div>
                        <p className="text-[15px] font-semibold text-on-surface mb-1">No matching suppliers found</p>
                        <p className="text-secondary text-[11px] max-w-[160px] mb-4">Refine your search parameters for more specific results.</p>
                      </div>
                    ) : (
                      <div className="space-y-3.5">
                        {/* Inset Selection Bar */}
                        {isMultiSelectActive && (
                          <div className="bg-white border border-slate-200/90 rounded-2xl p-3 sm:px-4 sm:py-2.5 shadow-xs flex flex-wrap items-center justify-between gap-3 animate-in fade-in slide-in-from-top-2 duration-200">
                            {/* Left: Select all checkbox & selection counter */}
                            <div className="flex items-center gap-3 min-w-0">
                              <label className="flex items-center gap-2 cursor-pointer select-none group">
                                <input
                                  type="checkbox"
                                  checked={isAllSelected}
                                  onChange={handleSelectAllToggle}
                                  className="size-4 rounded accent-[#be001e] cursor-pointer"
                                />
                                <span className="text-[13px] font-bold text-slate-800 group-hover:text-[#be001e] transition-colors">
                                  Select all ({mergedSuppliers.length})
                                </span>
                              </label>
                              {selectedCompanyIds.length > 0 && (
                                <span className="text-xs font-semibold text-slate-500 hidden sm:inline-flex items-center gap-1.5 pl-3 border-l border-slate-200">
                                  <span className="inline-flex size-1.5 rounded-full bg-[#be001e]" />
                                  {selectedCompanyIds.length} of {mergedSuppliers.length} selected
                                </span>
                              )}
                            </div>

                            {/* Right: Actions (Cancel, Request Quote) */}
                            <div className="flex items-center gap-1 sm:gap-2 shrink-0 ml-auto sm:ml-0">
                              <button
                                type="button"
                                onClick={handleCancelMultiSelect}
                                className="px-2.5 sm:px-3.5 py-1.5 text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-full transition-colors cursor-pointer"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                disabled={selectedCompanyIds.length === 0}
                                onClick={handleRequestQuoteMulti}
                                className="flex items-center gap-1.5 bg-[#be001e] hover:bg-[#be001e]/90 active:scale-95 text-white text-xs font-bold px-3 sm:px-4 py-1.5 rounded-full shadow-sm transition disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100 cursor-pointer shrink-0"
                              >
                                <Send className="size-3.5 hidden sm:block" />
                                <span className="hidden sm:inline">Request Quote ({selectedCompanyIds.length})</span>
                                <span className="inline sm:hidden">Request ({selectedCompanyIds.length})</span>
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                          {mergedSuppliers.map((supplier) => (
                            <SupplierCard
                              key={supplier.id}
                              supplier={supplier}
                              isMultiSelectActive={isMultiSelectActive}
                              isSelected={selectedCompanyIds.includes(supplier.id)}
                              isActiveTarget={false}
                              onSelectToggle={() => handleSelectToggle(supplier.id)}
                              onViewDetails={() => {
                                if (!user && (supplier.type === "lead")) {
                                  setShowLeadAuthPrompt(true)
                                  return
                                }
                                const rawId = supplier.type === "internal"
                                  ? supplier.id
                                  : supplier.id.replace("lead-", "")
                                navigate({
                                  to: "/explore/companies/$companyId",
                                  params: { companyId: rawId },
                                  search: {
                                    companyType: supplier.type as "internal" | "lead",
                                    companyName: supplier.name || undefined,
                                  },
                                })
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Products Tab */}
                {activeTab === "products" && (
                  <>
                    {selectedDetailProductId ? (
                      <ProductDetailsView
                        productId={selectedDetailProductId}
                        highlightVariantId={highlightVariantId}
                        onBack={() => {
                          navigate({
                            search: (prev) => {
                              const { productId, highlightVariantId, ...rest } = prev as any
                              return rest
                            }
                          } as any)
                        }}
                        companyName={companyMap.get(
                          data?.products?.find((p) => p.productId === selectedDetailProductId)?.companyId || ""
                        )}
                        onQuickInquiry={(prod) => {
                          setActiveProductRfqTarget({
                            type: "single-product",
                            product: prod,
                            companyName: companyMap.get(prod.companyId),
                          })
                        }}
                      />
                    ) : (!data?.products || data.products.length === 0) && (!data?.externalProducts || data.externalProducts.length === 0) ? (
                      <div className="rounded-2xl border border-dashed border-outline-variant bg-surface-container-low/40 p-16 flex flex-col items-center justify-center text-center opacity-70">
                        <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-4 border border-outline-variant/20 shadow-sm">
                          <Package className="size-5 text-secondary" />
                        </div>
                        <p className="text-[15px] font-semibold text-on-surface mb-1">No matching products found</p>
                        <p className="text-secondary text-[11px] max-w-[160px] mb-4">Try a different keyword or adjust filters.</p>
                      </div>
                    ) : (
                      <>
                        <h2 className="text-[24px] font-semibold tracking-[-0.015em] text-on-surface">Top Product Matches</h2>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                          {data?.products?.map((product) => (
                            <ProductSearchCard
                              key={product.id}
                              product={product}
                              companyName={companyMap.get(product.companyId)}
                              isActiveTarget={false}
                              onRequestQuote={() => {
                                navigate({
                                  to: "/explore/products/$productId",
                                  params: { productId: product.productId || product.id },
                                  search: {
                                    highlightVariantId: product.variantId || product.id,
                                    ...(companyMap.get(product.companyId) ? { companyName: companyMap.get(product.companyId) } : {}),
                                    ...(product.productName || (product as any).name ? { productName: product.productName || (product as any).name } : {}),
                                  },
                                })
                              }}
                            />
                          ))}
                          {data?.externalProducts?.map((extProduct) => (
                            <ProductSearchCard
                              key={extProduct.id}
                              product={extProduct}
                              onRequestQuote={() => {
                                setActiveProductRfqTarget({
                                  type: "single-external",
                                  seller: {
                                    id: extProduct.externalSellerId || extProduct.id,
                                    sellerName: extProduct.companyName || "External Seller",
                                    productName: extProduct.productName,
                                  }
                                })
                              }}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
                  </>
                )}
              </div>
            )}
          </div>
      {showLeadAuthPrompt && (
        <PromptModalCard
          title="Log in to view supplier details"
          description="Create a free account or log in to view verified lead seller details, contact information, and business credentials."
          onCancel={() => setShowLeadAuthPrompt(false)}
          onCancelText="Keep exploring"
        />
      )}
    </div>
  )
}